import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, X, Clock, Search, User, Sparkles, Menu, Edit, MapPin, Shield, MessageCircle, List, Grid3X3, LayoutDashboard, ArrowLeft, LogOut, Trash2, Phone, Mail, Check, ChevronRight, Store, LayoutGrid, UtensilsCrossed, Wheat, Cookie, Coffee, CupSoda, Soup, Pizza, ShoppingBag, Layers } from 'lucide-react';
import api from '../services/api';
import { cacheService, useCachedData } from '../services/cacheService';
import { usePreloadPage } from '../services/preloadService';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../hooks/useCart';
import EnderecoEntrega from '../components/EnderecoEntrega';
import { Endereco } from '../hooks/useFrete';
import ModalPagamento from '../components/ModalPagamento';
import HistoricoPedidos from '../components/HistoricoPedidos';
import { config } from '../config/environment';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import { usePromocoes } from '../hooks/usePromocoes';

// Declaração de tipos para Meta Pixel
declare global {
  interface Window {
    fbq: (action: string, event: string, parameters?: any) => void;
  }
}

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  preco_promocional?: number;
  promocao?: boolean;
  imagem_url: string;
  categoria_nome: string;
  tempo_preparo: number;
  destaque: boolean;
  opcoes_personalizacao?: OpcaoPersonalizacao[];
}

interface OpcaoPersonalizacao {
  id: number;
  nome: string;
  tipo: 'adicionar' | 'remover'; // Tipo específico para adicionar ou remover ingredientes
  obrigatorio: boolean;
  max_selecoes: number;
  itens: {
    id: number;
    nome: string;
    preco_adicional: number;
    quantidade: number;
  }[];
}

// Interface para itens do carrinho (compatibilidade com modal)
interface ItemCarrinho {
  produto: Produto;
  quantidade: number;
  personalizacoes: {
    opcao_id: number;
    item_id: number;
    nome: string;
    preco_adicional: number;
    quantidade: number;
    tipo: 'adicionar' | 'remover';
  }[];
  observacoes: string;
}

// ─── DEFINIÇÃO DE PALETAS DE CORES DO CARDÁPIO ────────────────────────
export interface KRPalette {
  id: string;
  name: string;
  badge: string;
  tagline: string;
  previewDots: [string, string, string];
  isDark: boolean;
  colors: {
    bgApp: string;
    bgCard: string;
    borderCard: string;
    bgHeader: string;
    borderHeader: string;
    textPrimary: string;
    textMuted: string;
    primary: string;
    primaryHover: string;
    primaryText: string;
    primaryBorder: string;
    primarySubtle: string;
    primarySubtleText: string;
    accent: string;
    price: string;
    bgSurfaceElevated?: string;
  };
}

export const KR_PALETTES: KRPalette[] = [
  {
    id: 'bordo-nobre',
    name: 'Bordô Nobre & Ouro',
    badge: 'Identidade KR',
    tagline: 'Cores do logo KR: Vinho Nobre, Dourado e fundo Marfim',
    previewDots: ['#701428', '#c59b27', '#f8f5ee'],
    isDark: false,
    colors: {
      bgApp: '#f8f5ee',
      bgCard: '#ffffff',
      borderCard: '#e7ded1',
      bgHeader: 'rgba(255, 255, 255, 0.98)',
      borderHeader: '#e2d5c3',
      textPrimary: '#1f1618',
      textMuted: '#6e5d59',
      primary: '#701428',
      primaryHover: '#871932',
      primaryText: '#ffffff',
      primaryBorder: '#540d1e',
      primarySubtle: 'rgba(112, 20, 40, 0.12)',
      primarySubtleText: '#701428',
      accent: '#c59b27',
      price: '#701428',
      bgSurfaceElevated: '#f4ede1'
    }
  },
  {
    id: 'warm-artisan',
    name: 'Warm Artisan',
    badge: 'Pão Quente',
    tagline: 'Padaria Artesanal: Terracota tostado, trigo e café expresso',
    previewDots: ['#c2410c', '#d97706', '#f7f2ea'],
    isDark: false,
    colors: {
      bgApp: '#f7f2ea',
      bgCard: '#ffffff',
      borderCard: '#ebdcc9',
      bgHeader: 'rgba(255, 255, 255, 0.98)',
      borderHeader: '#e3cfb6',
      textPrimary: '#261a12',
      textMuted: '#786558',
      primary: '#c2410c',
      primaryHover: '#9a3412',
      primaryText: '#ffffff',
      primaryBorder: '#9a3412',
      primarySubtle: 'rgba(194, 65, 12, 0.12)',
      primarySubtleText: '#c2410c',
      accent: '#d97706',
      price: '#c2410c',
      bgSurfaceElevated: '#f0e6d6'
    }
  },
  {
    id: 'dark-gourmet',
    name: 'Dark Gourmet',
    badge: 'Laranja Flame',
    tagline: 'Fundo ardósia escuro, Laranja Flame e alto contraste gastronômico',
    previewDots: ['#0b0f17', '#f97316', '#1e293b'],
    isDark: true,
    colors: {
      bgApp: '#0b0f17',
      bgCard: '#131b2e',
      borderCard: 'rgba(255, 255, 255, 0.08)',
      bgHeader: 'rgba(11, 15, 23, 0.96)',
      borderHeader: 'rgba(255, 255, 255, 0.08)',
      textPrimary: '#f8fafc',
      textMuted: '#94a3b8',
      primary: '#f97316',
      primaryHover: '#ea580c',
      primaryText: '#ffffff',
      primaryBorder: '#c2410c',
      primarySubtle: 'rgba(249, 115, 22, 0.15)',
      primarySubtleText: '#fb923c',
      accent: '#f97316',
      price: '#ffffff',
      bgSurfaceElevated: '#1a233a'
    }
  },
  {
    id: 'delivery-turbo',
    name: 'Delivery Turbo',
    badge: 'Estilo iFood',
    tagline: 'Vermelho vibrante de alta conversão e contraste imediato',
    previewDots: ['#ea1d2c', '#ffffff', '#f4f5f7'],
    isDark: false,
    colors: {
      bgApp: '#f4f5f7',
      bgCard: '#ffffff',
      borderCard: '#e5e7eb',
      bgHeader: 'rgba(255, 255, 255, 0.98)',
      borderHeader: '#e5e7eb',
      textPrimary: '#111827',
      textMuted: '#4b5563',
      primary: '#ea1d2c',
      primaryHover: '#c81321',
      primaryText: '#ffffff',
      primaryBorder: '#b91c1c',
      primarySubtle: 'rgba(234, 29, 44, 0.1)',
      primarySubtleText: '#ea1d2c',
      accent: '#ea1d2c',
      price: '#ea1d2c',
      bgSurfaceElevated: '#e5e7eb'
    }
  },
  {
    id: 'cafe-colonial',
    name: 'Café Colonial',
    badge: 'Rústico Nobre',
    tagline: 'Confeitaria tradicional: Cacau, canela e baunilha',
    previewDots: ['#52311f', '#a06637', '#f5eee3'],
    isDark: false,
    colors: {
      bgApp: '#f5eee3',
      bgCard: '#ffffff',
      borderCard: '#e4d5be',
      bgHeader: 'rgba(255, 255, 255, 0.98)',
      borderHeader: '#dcc9af',
      textPrimary: '#2b1b11',
      textMuted: '#6d594b',
      primary: '#52311f',
      primaryHover: '#3f2415',
      primaryText: '#fef9ee',
      primaryBorder: '#382012',
      primarySubtle: 'rgba(82, 49, 31, 0.12)',
      primarySubtleText: '#52311f',
      accent: '#a06637',
      price: '#52311f',
      bgSurfaceElevated: '#ece1d0'
    }
  },
  {
    id: 'original',
    name: 'KR Original',
    badge: 'Atual (Pêssego)',
    tagline: 'Paleta padrão anterior com tons pêssego suave',
    previewDots: ['#fcc3ac', '#4a1c0d', '#F6F7F9'],
    isDark: false,
    colors: {
      bgApp: '#F6F7F9',
      bgCard: '#ffffff',
      borderCard: 'rgba(229, 231, 235, 0.8)',
      bgHeader: 'rgba(255, 255, 255, 0.95)',
      borderHeader: 'rgba(229, 231, 235, 0.8)',
      textPrimary: '#111827',
      textMuted: '#6b7280',
      primary: '#fcc3ac',
      primaryHover: '#f7b399',
      primaryText: '#4a1c0d',
      primaryBorder: 'rgba(245, 178, 152, 0.6)',
      primarySubtle: 'rgba(252, 195, 172, 0.35)',
      primarySubtleText: '#4a1c0d',
      accent: '#b85836',
      price: '#111827',
      bgSurfaceElevated: '#f3f4f6'
    }
  }
];

// Interface para o modal de customização
interface ModalCustomizacao {
  show: boolean;
  itemIndex: number | null;
  item: any | null;
}

// Função para gerar slug a partir do nome
const gerarSlug = (nome: string): string => {
  if (!nome) return '';
  return nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '');
};

// Função para verificar se é ID numérico
const isNumericId = (str: string): boolean => {
  return /^\d+$/.test(str);
};

// Função para remover acentos e normalizar para busca
const removerAcentos = (texto: string): string => {
  if (!texto) return '';
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};
// [x] Auditoria de sintaxe final no `MenuPublico.tsx`
// [x] Remoção de declarações residuais duplicadas
// [x] Verificação de escopo (Temporal Dead Zone) para hooks
// [x] Validação final do sistema de modais em cascata
// [x] Consolidação da lógica de loading e dados da empresa

const MenuPublico: React.FC = () => {
  const { empresaId } = useParams<{ empresaId: string }>();
  const navigate = useNavigate();
  const { usuario, atualizarPerfil, carregarPerfil, logout } = useAuth();

  // Hook do carrinho - usar ID numérico se disponível
  const empresaIdNumerico = isNumericId(empresaId || '') ? parseInt(empresaId || '1') : 1;
  const cart = useCart(empresaIdNumerico);

  // Hook para promoções
  const promocoes = usePromocoes(empresaIdNumerico);

  // Hook para detecção de dispositivo
  const { isMobile, isTablet } = useMobileOptimizations();

  // ─── ESTADOS DE UI E DISPOSITIVO ────────────────────────────────────
  const [screenWidth, setScreenWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);
  const [headerHeight, setHeaderHeight] = useState<number>(150);
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [showMobileSearch, setShowMobileSearch] = useState<boolean>(false);
  const [showMobileMenuTip, setShowMobileMenuTip] = useState<boolean>(false);
  const [visualizacaoMobile, setVisualizacaoMobile] = useState<'lista' | 'grade'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('visualizacaoMobile');
      if (saved === 'lista' || saved === 'grade') return saved;
      // Define padrão inicial: 'lista' para mobile (< 768px), 'grade' para iPad/Desktop
      return window.innerWidth < 768 ? 'lista' : 'grade';
    }
    return 'grade';
  });

  // Salvar no localStorage sempre que visualizacaoMobile mudar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('visualizacaoMobile', visualizacaoMobile);
    }
  }, [visualizacaoMobile]);

  // ─── TEMA DARK GOURMET DEFINITIVO ──────────────────────────────────
  const selectedThemeId = 'dark-gourmet';

  const activeTheme = useMemo(() => {
    return KR_PALETTES.find(p => p.id === 'dark-gourmet') || KR_PALETTES[0];
  }, []);

  // Aplicar variáveis CSS diretamente no elemento raiz do documento para refletir em toda a página
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    document.body.style.backgroundColor = activeTheme.colors.bgApp;
    root.style.setProperty('--bg-app', activeTheme.colors.bgApp);
    root.style.setProperty('--bg-card', activeTheme.colors.bgCard);
    root.style.setProperty('--border-card', activeTheme.colors.borderCard);
    root.style.setProperty('--bg-header', activeTheme.colors.bgHeader);
    root.style.setProperty('--border-header', activeTheme.colors.borderHeader);
    root.style.setProperty('--text-primary', activeTheme.colors.textPrimary);
    root.style.setProperty('--text-muted', activeTheme.colors.textMuted);
    root.style.setProperty('--color-primary', activeTheme.colors.primary);
    root.style.setProperty('--color-primary-hover', activeTheme.colors.primaryHover);
    root.style.setProperty('--color-primary-text', activeTheme.colors.primaryText);
    root.style.setProperty('--color-primary-border', activeTheme.colors.primaryBorder);
    root.style.setProperty('--color-primary-subtle', activeTheme.colors.primarySubtle);
    root.style.setProperty('--color-primary-subtle-text', activeTheme.colors.primarySubtleText);
    root.style.setProperty('--color-accent', activeTheme.colors.accent);
    root.style.setProperty('--color-price', activeTheme.colors.price);
    root.style.setProperty('--bg-surface-elevated', activeTheme.colors.bgSurfaceElevated || activeTheme.colors.bgCard);
  }, [activeTheme]);

  // ─── ESTADOS DE DADOS DA EMPRESA E PRODUTOS ──────────────────────────
  const [empresa, setEmpresa] = useState<any>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categorias, setCategorias] = useState<{ nome: string; imagem_url?: string; ordem: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [lojaAberta, setLojaAberta] = useState<boolean>(true);
  const [statusLoja, setStatusLoja] = useState<any>(null);
  const [statusLojaCarregando, setStatusLojaCarregando] = useState<boolean>(true);
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [mostrarTodos, setMostrarTodos] = useState<boolean>(false);
  const [busca, setBusca] = useState<string>('');
  const [produtosVisiveisPorCategoria, setProdutosVisiveisPorCategoria] = useState<{ [key: string]: number }>({});
  const [mesaGarcom, setMesaGarcom] = useState<string>('');
  const verificandoStatusRef = useRef(false);

  // ─── ESTADOS DE CARRINHO E PEDIDO ────────────────────────────────────
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null);
  const [opcoes, setOpcoes] = useState<OpcaoPersonalizacao[]>([]);
  const [quantidade, setQuantidade] = useState(1);
  const [observacoes, setObservacoes] = useState('');
  const [selectedOpcoes, setSelectedOpcoes] = useState<{ [key: number]: { itemId: number; quantidade: number }[] }>({});
  const [quantidadesProdutos, setQuantidadesProdutos] = useState<{ [key: number]: number }>({});
  const [cupomAplicado, setCupomAplicado] = useState<{ codigo: string; promocao: any; desconto: number } | null>(null);
  const [enderecoEntrega, setEnderecoEntrega] = useState<Endereco | null>(null);
  const [valorFrete, setValorFrete] = useState<number>(0);
  const [tipoEntrega, setTipoEntrega] = useState<'entrega' | 'retirada'>('entrega');

  // ─── ESTADOS DE CUSTOMIZAÇÃO ─────────────────────────────────────────
  const [modalCustomizacao, setModalCustomizacao] = useState<ModalCustomizacao>({
    show: false,
    itemIndex: null,
    item: null
  });
  const [opcoesCustomizacao, setOpcoesCustomizacao] = useState<OpcaoPersonalizacao[]>([]);
  const [personalizacoesCustomizacao, setPersonalizacoesCustomizacao] = useState<{ [key: number]: { itemId: number; quantidade: number }[] }>({});
  const [observacoesCustomizacao, setObservacoesCustomizacao] = useState<string>('');

  // ─── ESTADOS DE PERFIL E PIXEL ───────────────────────────────────────
  const [editandoPerfil, setEditandoPerfil] = useState<boolean>(false);
  const [dadosPerfil, setDadosPerfil] = useState({
    nome: '', sobrenome: '', telefone: '', rua: '', bairro: '', numero_casa: '', cep: '', cidade: ''
  });
  const [pixelId, setPixelId] = useState<string | null>(null);
  const [pixelLoaded, setPixelLoaded] = useState<boolean>(false);

  // ─── SISTEMA DE PILHA DE MODAIS (NAVEGAÇÃO EM CASCATA) ───────────────
  const [modalStack, setModalStack] = useState<string[]>([]);
  const modalAtual = modalStack[modalStack.length - 1];
  const algumModalAberto = modalStack.length > 0;

  const abrirModal = (nome: string) => setModalStack(prev => [...prev, nome]);
  const voltarModal = () => setModalStack(prev => prev.slice(0, -1));
  const fecharTodosModais = () => setModalStack([]);

  // Atalhos de compatibilidade
  const showCarrinho = modalAtual === 'carrinho';
  const setShowCarrinho = (v: boolean) => v ? abrirModal('carrinho') : fecharTodosModais();
  const showModal = modalAtual === 'produto';
  const setShowModal = (v: boolean) => v ? abrirModal('produto') : voltarModal();
  const showPerfilModal = modalAtual === 'perfil' || (modalStack.includes('perfil') && !['historico','excluir-conta'].includes(modalAtual));
  const setShowPerfilModal = (v: boolean) => v ? abrirModal('perfil') : voltarModal();
  const showHistoricoPedidos = modalAtual === 'historico';
  const setShowHistoricoPedidos = (v: boolean) => v ? abrirModal('historico') : voltarModal();
  const showExcluirContaModal = modalAtual === 'excluir-conta';
  const setShowExcluirContaModal = (v: boolean) => v ? abrirModal('excluir-conta') : voltarModal();
  const showChamarGarcom = modalAtual === 'chamar-garcom';
  const setShowChamarGarcom = (v: boolean) => v ? abrirModal('chamar-garcom') : voltarModal();
  const showModalPagamento = modalAtual === 'pagamento';
  const setShowModalPagamento = (v: boolean) => v ? abrirModal('pagamento') : voltarModal();
  const showModalEscolhaCustomizacao = modalAtual === 'escolha-customizacao';
  const setShowModalEscolhaCustomizacao = (v: boolean) => v ? abrirModal('escolha-customizacao') : voltarModal();
  const showModalCustomizacaoAtivo = modalAtual === 'customizacao';
  const setShowModalCustomizacaoAtivo = (v: boolean) => v ? abrirModal('customizacao') : voltarModal();
  const showLoginCard = modalAtual === 'login';
  const setShowLoginCard = (v: boolean) => v ? abrirModal('login') : fecharTodosModais();
  const showModalLojaFechada = modalAtual === 'loja-fechada';
  const setShowModalLojaFechada = (v: boolean) => v ? abrirModal('loja-fechada') : fecharTodosModais();

  // ─── MEMOS E CONFIGURAÇÕES ──────────────────────────────────────────
  const cartRightOffset = useMemo(() => '1rem', []);
  const WHATSAPP_NUMERO = useMemo(() => empresa?.whatsapp?.replace(/\D/g, '') || '5554992159272', [empresa?.whatsapp]);

  const modalMaxHeightStyle = useMemo(() => {
    if (deviceType === 'mobile') {
      return { 
        maxHeight: '100dvh', // Usar dynamic viewport height
        height: '100dvh',
        borderRadius: '0' 
      };
    }
    if (deviceType === 'tablet') return { maxHeight: '88vh' };
    return { maxHeight: '82vh' }; // Desktop/Notebook: proporcional e compacto
  }, [deviceType]);

  const modalWidthStyle = useMemo(() => {
    if (deviceType === 'mobile') return { width: '100vw', maxWidth: '100vw' };
    if (deviceType === 'tablet') return { width: 'min(88vw, 620px)' };
    return { width: 'min(50vw, 580px)' }; // Desktop: compacto e centralizado
  }, [deviceType]);

  // ─── EFEITOS (EFFECTS) ──────────────────────────────────────────────
  
  // Detectar tipo de dispositivo e calcular altura do header
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const width = window.innerWidth;
    const isIPadWidth = width >= 768 && width <= 1366;
    const isIPadUserAgent = /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 0;
    const isIPadDevice = isIPadWidth || isIPadUserAgent;

    let newDeviceType: 'mobile' | 'tablet' | 'desktop';
    let newHeaderHeight: number;

    if (isMobile && !isIPadDevice) {
      newDeviceType = 'mobile';
      newHeaderHeight = 54;
    } else if (isTablet || isIPadDevice) {
      newDeviceType = 'tablet';
      newHeaderHeight = 58;
    } else {
      newDeviceType = 'desktop';
      newHeaderHeight = 64;
    }

    setDeviceType(newDeviceType);
    setHeaderHeight(newHeaderHeight);
  }, [isMobile, isTablet, screenWidth]);

  // Atualizar altura do header após renderização e em mudanças de tamanho/conteúdo
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerElement = document.querySelector('[data-header-container]') as HTMLElement;
      if (headerElement) {
        const computedHeight = headerElement.offsetHeight;
        setHeaderHeight(computedHeight);
      }
    };

    const timeoutId = setTimeout(updateHeaderHeight, 100);
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [deviceType, empresa?.nome, usuario, lojaAberta, selectedThemeId]);




  // Bloquear scroll quando qualquer modal estiver aberto
  // position:fixed é necessário para iOS Safari não resetar scroll para o topo
  useEffect(() => {
    if (algumModalAberto) {
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      const preventTouchMove = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const isScrollable = target.closest('.overflow-y-auto') || 
                             target.closest('[data-modal-content]') ||
                             target.closest('.carrinho-scroll');
        
        if (!isScrollable) {
          e.preventDefault();
        }
      };

      const preventWheel = (e: WheelEvent) => {
        const target = e.target as HTMLElement;
        const isModalContent =
          target.closest('[data-modal-content]') !== null ||
          target.closest('[role="dialog"]') !== null ||
          target.closest('.carrinho-scroll') !== null ||
          target.hasAttribute('data-modal-content');
        if (!isModalContent) e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      document.addEventListener('wheel', preventWheel, { passive: false });

      return () => {
        // Lê o valor salvo diretamente do style para evitar closure stale
        const savedY = Math.abs(parseInt(document.body.style.top || '0', 10));

        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.height = '';

        // 'instant' evita qualquer animação de scroll visível ao fechar o modal
        window.scrollTo({ top: savedY, behavior: 'instant' as ScrollBehavior });

        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('wheel', preventWheel);
      };
    }
  }, [algumModalAberto]);

  // Usar cache para produtos e categorias
  const { data: menuData, loading: menuLoading, refetch: refetchMenu } = useCachedData(
    `menu_${empresaId}`,
    async () => {
      const [produtosResponse, categoriasResponse] = await Promise.all([
        api.get(`/api/menu/${empresaId}`),
        api.get(`/api/categorias/${empresaId}`)
      ]);

      return {
        produtos: produtosResponse.data,
        categorias: categoriasResponse.data
          .sort((a: any, b: any) => a.ordem - b.ordem)
          .map((cat: any) => ({
            nome: cat.nome,
            imagem_url: cat.imagem_url,
            ordem: cat.ordem
          }))
      };
    },
    2 * 60 * 1000, // Reduzido para 2 minutos para maior agilidade nas atualizações
    [empresaId]
  );

  // Usar cache para dados da empresa
  const { data: empresaData, refetch: refetchEmpresa } = useCachedData(
    `empresa_${empresaId}`,
    async () => {
      const response = await api.get(`/api/empresa/${empresaId}/public`);
      return response.data;
    },
    5 * 60 * 1000, // Reduzido para 5 minutos
    [empresaId]
  );

  // Pré-carregar páginas relacionadas
  usePreloadPage('menu-publico', empresaIdNumerico);

  // Atualizar dados quando o cache carregar
  useEffect(() => {
    if (menuData) {
      setProdutos(menuData.produtos);
      setCategorias(menuData.categorias);
    }
  }, [menuData]);

  // Atualizar loading baseado no cache
  useEffect(() => {
    setLoading(menuLoading);
  }, [menuLoading]);

  useEffect(() => {
    if (empresaData) {
      setEmpresa(empresaData);

      // Atualizar URL para usar slug se estiver usando ID numérico
      if (isNumericId(empresaId || '') && empresaData.nome) {
        const slug = gerarSlug(empresaData.nome);
        if (slug && slug !== empresaId) {
          // Atualizar URL sem recarregar a página
          window.history.replaceState({}, '', `/menu/${slug}`);
        }
      }
    }
  }, [empresaData, empresaId]);

  // Verificar status da loja ANTES de renderizar completamente
  useEffect(() => {
    if (empresaId) {
      // Verificar status da loja primeiro (prioridade)
      checkHorarioFuncionamento();
      // Depois buscar outras coisas
      fetchPixelId();
    }
  }, [empresaId]);

  // Verificar novamente quando os dados da empresa forem carregados (garantir sincronização)
  // Só verificar se ainda não foi verificado (statusLojaCarregando ainda é true)
  useEffect(() => {
    if (empresaData && empresaId && statusLojaCarregando) {
      checkHorarioFuncionamento();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaData, empresaId]); // Não incluir statusLojaCarregando para evitar loop

  // Função para buscar Pixel ID
  const fetchPixelId = async () => {
    try {
      const response = await api.get(`/api/meta-pixel/${empresaId}`);
      if (response.data.pixel_id) {
        setPixelId(response.data.pixel_id);
        loadMetaPixel(response.data.pixel_id);
      }
    } catch (error) {
      console.error('Erro ao buscar Pixel ID:', error);
    }
  };

  // Função para carregar o Meta Pixel
  const loadMetaPixel = (pixelId: string) => {
    if (pixelLoaded) return;

    // Verificar se o pixel já foi carregado
    if (typeof window.fbq === 'function') {
      setPixelLoaded(true);
      return;
    }

    // Carregar o script do Meta Pixel
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    setPixelLoaded(true);
  };

  // Função para disparar eventos do Meta Pixel
  const trackPixelEvent = (eventName: string, parameters?: any) => {
    if (pixelId && typeof window.fbq === 'function') {
      window.fbq('track', eventName, parameters);
      console.log(`Meta Pixel Event: ${eventName}`, parameters);
    }
  };

  // Função para verificar horário de funcionamento com retry logic
  const checkHorarioFuncionamento = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 segundo

    // Prevenir múltiplas verificações simultâneas
    if (verificandoStatusRef.current && retryCount === 0) {
      console.log('⏸️ Verificação já em andamento, ignorando...');
      return;
    }
    verificandoStatusRef.current = true;

    try {
      setStatusLojaCarregando(true);
      // Adicionado timestamp ?t= para evitar cache do navegador/proxy e garantir status real
      const response = await api.get(`/api/horario-funcionamento/status/${empresaId}?t=${Date.now()}`);

      // Validar resposta antes de atualizar estado
      if (response && response.data && typeof response.data.aberto === 'boolean') {
        // Se não há configuração de horários, sempre assumir aberta
        if (response.data.semConfiguracao === true) {
          console.log('⚠️ Horários não configurados - assumindo loja aberta');
          setLojaAberta(true);
          setStatusLoja(response.data);
          setStatusLojaCarregando(false);
          verificandoStatusRef.current = false;
          return;
        }

        setStatusLoja(response.data);
        setLojaAberta(response.data.aberto);
        setStatusLojaCarregando(false);
        verificandoStatusRef.current = false;
      } else {
        // Resposta inválida - tentar novamente
        console.warn('⚠️ Resposta inválida do servidor, tentando novamente...', response.data);
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            checkHorarioFuncionamento(retryCount + 1);
          }, RETRY_DELAY);
        } else {
          // Após várias tentativas, assumir que está aberta para não bloquear usuário
          console.error('❌ Não foi possível verificar status da loja após várias tentativas. Assumindo que está aberta.');
          setLojaAberta(true);
          setStatusLojaCarregando(false);
          verificandoStatusRef.current = false;
        }
      }
    } catch (error: any) {
      console.error('❌ Erro ao verificar horário de funcionamento:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        empresaId
      });

      // Em caso de erro do servidor (500), assumir aberta imediatamente
      if (error.response?.status === 500) {
        console.warn('⚠️ Erro 500 do servidor - assumindo loja aberta');
        setLojaAberta(true);
        setStatusLojaCarregando(false);
        verificandoStatusRef.current = false;
        return;
      }

      // Se for erro de rede ou timeout, tentar novamente
      if (retryCount < MAX_RETRIES && (
        error.code === 'ECONNABORTED' ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error') ||
        !error.response
      )) {
        setTimeout(() => {
          checkHorarioFuncionamento(retryCount + 1);
        }, RETRY_DELAY);
      } else {
        // Após várias tentativas ou erro definitivo, assumir que a loja está aberta
        // para não bloquear o usuário desnecessariamente
        console.warn('⚠️ Assumindo que a loja está aberta devido a erro na verificação');
        setLojaAberta(true);
        setStatusLojaCarregando(false);
        verificandoStatusRef.current = false;
      }
    }
  }, [empresaId]);

  // Efeito para POLLING do status da loja (Verifica a cada 60 segundos)
  useEffect(() => {
    if (!empresaId) return;

    // Criar um intervalo para verificar o status periodicamente a cada 15 segundos
    const intervalId = setInterval(() => {
      checkHorarioFuncionamento();
    }, 15000); // 15 segundos para resposta mais rápida ao PDV

    return () => {
      clearInterval(intervalId);
    };
  }, [empresaId, checkHorarioFuncionamento]);





  // Filtrar produtos
  const produtosFiltrados = useMemo(() => {
    let filtrados = produtos;

    // Se há uma busca ativa, mostrar todos os produtos que correspondem à busca
    if (busca.trim()) {
      const keywords = removerAcentos(busca).split(/\s+/).filter(k => k.length > 0);
      
      filtrados = filtrados.filter(produto => {
        const nomeNorm = removerAcentos(produto.nome);
        const descNorm = removerAcentos(produto.descricao || '');
        const catNorm = removerAcentos(produto.categoria_nome);
        const searchPool = `${nomeNorm} ${descNorm} ${catNorm}`;
        
        // Verifica se todas as palavras da busca estão no pool de informações do produto
        return keywords.every(kw => searchPool.includes(kw));
      });
    } else {
      // Filtrar por categoria apenas se não há busca
      if (categoriaFiltro && !mostrarTodos) {
        filtrados = filtrados.filter(produto => produto.categoria_nome === categoriaFiltro);
      }
    }

    return filtrados;
  }, [produtos, categoriaFiltro, busca, mostrarTodos]);

  // Produtos agrupados por categoria
  const produtosPorCategoria = useMemo(() => {
    const grupos: { [key: string]: Produto[] } = {};
    produtosFiltrados.forEach(produto => {
      if (!grupos[produto.categoria_nome]) {
        grupos[produto.categoria_nome] = [];
      }
      grupos[produto.categoria_nome].push(produto);
    });
    return grupos;
  }, [produtosFiltrados]);

  // Função para alternar entre ver tudo e categoria específica
  const toggleVerTudo = () => {
    if (mostrarTodos) {
      setMostrarTodos(false);
      setCategoriaFiltro('');
    } else {
      setMostrarTodos(true);
      setCategoriaFiltro('');
    }
    setBusca(''); // Limpar busca ao alternar
    setShowMobileSearch(false); // Fechar busca mobile
  };

  // Função para detectar se é iPad (incluindo iPad Pro)
  const isIPad = () => {
    return screenWidth >= 768 && screenWidth <= 1366;
  };

  // Função para obter número de produtos iniciais baseado no dispositivo
  const getProdutosIniciais = () => {
    const isIPadDevice = isIPad();
    const produtos = isIPadDevice ? 6 : 4;
    return produtos;
  };

  // Função para obter incremento de produtos baseado no dispositivo
  const getIncrementoProdutos = () => {
    return isIPad() ? 6 : 4;
  };

  // Função para carregar mais produtos (4 por vez no mobile, 6 por vez no iPad)
  const carregarMaisProdutos = (categoria: string, totalProdutos: number) => {
    setProdutosVisiveisPorCategoria(prev => {
      const produtosIniciais = getProdutosIniciais();
      const incremento = getIncrementoProdutos();
      const atual = prev[categoria] || produtosIniciais;
      const proximo = Math.min(atual + incremento, totalProdutos);
      return {
        ...prev,
        [categoria]: proximo
      };
    });
  };

  // Função para resetar produtos visíveis
  const resetarProdutosVisiveis = (categoria: string) => {
    setProdutosVisiveisPorCategoria(prev => ({
      ...prev,
      [categoria]: getProdutosIniciais()
    }));
  };

  const handleProdutoClick = async (produto: Produto) => {
    // Verificar se a loja está aberta antes de permitir visualizar produto
    if (!lojaAberta) {
      setShowModalLojaFechada(true);
      return;
    }

    setSelectedProduto(produto);
    setQuantidade(1);
    setObservacoes('');
    setSelectedOpcoes({});
    abrirModal('produto');

    // Carregar opções de personalização do produto
    try {
      const response = await api.get(`/api/menu/produto/${produto.id}/opcoes`);
      setOpcoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar opções de personalização:', error);
      setOpcoes([]);
    }

    // Meta Pixel: ViewContent
    trackPixelEvent('ViewContent', {
      content_ids: [produto.id.toString()],
      content_name: produto.nome,
      content_category: produto.categoria_nome,
      value: produto.preco,
      currency: 'BRL'
    });
  };

  // Cálculo do preço total do produto selecionado (base + personalizações)
  const precoPersonalizacoes = useMemo(() => {
    return Object.entries(selectedOpcoes).reduce((total, [opcaoId, itens]) => {
      return total + itens.reduce((itemTotal, item) => {
        const opcao = opcoes.find(op => op.id === parseInt(opcaoId));
        const opcaoItem = opcao?.itens.find(i => i.id === item.itemId);
        return itemTotal + (opcaoItem?.preco_adicional || 0) * item.quantidade;
      }, 0);
    }, 0);
  }, [selectedOpcoes, opcoes]);

  const precoTotal = useMemo(() => {
    if (!selectedProduto) return 0;
    return (selectedProduto.preco + precoPersonalizacoes) * quantidade;
  }, [selectedProduto, precoPersonalizacoes, quantidade]);

  const handleOpcaoChange = (opcaoId: number, itemId: number, checked: boolean) => {
    setSelectedOpcoes(prev => {
      const current = prev[opcaoId] || [];
      let newSelection: { itemId: number; quantidade: number }[];

      if (checked) {
        newSelection = [...current, { itemId, quantidade: 1 }];
      } else {
        newSelection = current.filter(item => item.itemId !== itemId);
      }

      return {
        ...prev,
        [opcaoId]: newSelection
      };
    });
  };

  const handleQuantidadeChange = (opcaoId: number, itemId: number, novaQuantidade: number) => {
    if (novaQuantidade < 1) return;

    setSelectedOpcoes(prev => {
      const current = prev[opcaoId] || [];
      const newSelection = current.map(item =>
        item.itemId === itemId
          ? { ...item, quantidade: novaQuantidade }
          : item
      );
      return {
        ...prev,
        [opcaoId]: newSelection
      };
    });
  };

  // Função para adicionar ao carrinho diretamente do card (sem modal)
  const addToCartFromCard = async (produto: Produto, quantidadeCard: number) => {
    // Verificar se a loja está aberta
    if (!lojaAberta) {
      abrirModal('loja-fechada');
      return;
    }

    const precoFinal = produto.preco_promocional && produto.promocao ? produto.preco_promocional : produto.preco;

    // Adicionar ao carrinho sem personalizações (suporta visitante)
    const success = await cart.addToCart(
      produto.id,
      quantidadeCard,
      '',
      [],
      {
        nome: produto.nome,
        preco: precoFinal,
        imagem_url: produto.imagem_url,
        descricao: produto.descricao
      }
    );

    if (success) {
      toast.success(`${quantidadeCard}x ${produto.nome} adicionado${quantidadeCard > 1 ? 's' : ''} ao carrinho!`, {
        duration: 1500
      });

      // Meta Pixel: AddToCart
      trackPixelEvent('AddToCart', {
        content_ids: [produto.id.toString()],
        content_name: produto.nome,
        content_category: produto.categoria_nome,
        value: precoFinal * quantidadeCard,
        currency: 'BRL',
        num_items: quantidadeCard
      });

      // Resetar quantidade do produto no card
      setQuantidadesProdutos(prev => ({
        ...prev,
        [produto.id]: 1
      }));
    }
  };

  const addToCart = async () => {
    if (!selectedProduto) return;

    // Verificar se a loja está aberta
    if (!lojaAberta) {
      fecharTodosModais();
      setShowModalLojaFechada(true);
      return;
    }

    // Preparar personalizações para o formato esperado pela API
    const personalizacoes = Object.entries(selectedOpcoes).map(([opcaoId, itens]) =>
      itens.map(item => ({
        opcao_id: parseInt(opcaoId),
        item_id: item.itemId,
        nome: opcoes.find(op => op.id === parseInt(opcaoId))?.itens.find(i => i.id === item.itemId)?.nome || '',
        preco_adicional: opcoes.find(op => op.id === parseInt(opcaoId))?.itens.find(i => i.id === item.itemId)?.preco_adicional || 0,
        quantidade: item.quantidade,
        tipo: 'adicionar' as const
      }))
    ).flat();

    const precoFinal = selectedProduto.preco_promocional && selectedProduto.promocao 
      ? selectedProduto.preco_promocional 
      : selectedProduto.preco;

    // Adicionar ao carrinho usando o hook (suporta visitante)
    const success = await cart.addToCart(
      selectedProduto.id,
      quantidade,
      observacoes,
      personalizacoes,
      {
        nome: selectedProduto.nome,
        preco: precoFinal,
        imagem_url: selectedProduto.imagem_url,
        descricao: selectedProduto.descricao
      }
    );

    if (success) {
      // Ir para o carrinho: mantém o modal de produto na pilha embaixo do carrinho
      abrirModal('carrinho');

      // Meta Pixel: AddToCart
      const totalValue = (selectedProduto.preco +
        Object.entries(selectedOpcoes).reduce((total, [opcaoId, itens]) => {
          return total + itens.reduce((itemTotal, item) => {
            const opcao = opcoes.find(op => op.id === parseInt(opcaoId));
            const opcaoItem = opcao?.itens.find(i => i.id === item.itemId);
            return itemTotal + (opcaoItem?.preco_adicional || 0) * item.quantidade;
          }, 0);
        }, 0)) * quantidade;

      trackPixelEvent('AddToCart', {
        content_ids: [selectedProduto.id.toString()],
        content_name: selectedProduto.nome,
        content_category: selectedProduto.categoria_nome,
        value: totalValue,
        currency: 'BRL',
        num_items: quantidade
      });

      // Limpar estados
      setQuantidade(1);
      setObservacoes('');
      setSelectedOpcoes({});
    }
  };

  // Funções de gerenciamento do carrinho usando o hook
  const removeFromCart = async (itemId: number) => {
    await cart.removeItem(itemId);
  };

  const updateQuantity = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    await cart.updateItemQuantity(itemId, newQuantity);
  };

  const getTotalCarrinho = () => {
    // Verificar se tem direito à entrega grátis (primeira compra OU promoção ativa)
    const temPromocaoFreteGratis = promocoes.temDireitoFreteGratis(cart.cartData.total);
    const temDireitoEntregaGratis = temPromocaoFreteGratis || cart.cartData.entregaGratis;
    const freteFinal = temDireitoEntregaGratis ? 0 : valorFrete;

    // Aplicar desconto do cupom se houver
    const descontoCupom = cupomAplicado ? cupomAplicado.desconto : 0;
    const subtotal = cart.cartData.total + freteFinal;
    const totalComDesconto = Math.max(0, subtotal - descontoCupom);

    return totalComDesconto;
  };

  // Função para verificar se tem direito à entrega grátis (considerando valor mínimo configurável)
  // Funciona se: primeira compra OU promoção de frete grátis ativa com valor mínimo atingido
  const temDireitoEntregaGratis = () => {
    // Verificar se há promoção de frete grátis ativa e se atingiu o valor mínimo
    const temPromocaoFreteGratis = promocoes.temDireitoFreteGratis(cart.cartData.total);

    // Se tem promoção de frete grátis ativa, usar ela
    if (temPromocaoFreteGratis) {
      return true;
    }

    // Caso contrário, verificar se é primeira compra (compatibilidade)
    return cart.cartData.entregaGratis;
  };

  // Função para calcular quanto falta para atingir o valor mínimo (usando promoção configurável)
  const valorFaltanteParaFreteGratis = () => {
    // Se já tem direito por promoção, não falta nada
    if (promocoes.temDireitoFreteGratis(cart.cartData.total)) {
      return 0;
    }

    // Se é primeira compra, não falta nada
    if (cart.cartData.entregaGratis) {
      return 0;
    }

    // Caso contrário, calcular quanto falta para a promoção de frete grátis
    return promocoes.valorFaltanteFreteGratis(cart.cartData.total);
  };

  // Função para capitalizar a primeira letra
  const capitalizarPrimeiraLetra = (texto: string) => {
    return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
  };

  // Função para verificar se deve exibir a descrição
  const deveExibirDescricao = (produto: Produto): boolean => {
    if (!produto.descricao || produto.descricao.trim() === '') {
      return false;
    }
    // Não exibir se a descrição for igual ao nome (ignorando maiúsculas/minúsculas)
    if (produto.descricao.toLowerCase().trim() === produto.nome.toLowerCase().trim()) {
      return false;
    }
    return true;
  };

  // Função para verificar se o usuário tem cargo administrativo
  const temCargo = useMemo(() => {
    if (!usuario) return false;
    return (usuario.role &&
      (usuario.role === 'gestao' ||
        usuario.role === 'colaborador' ||
        usuario.role === 'caixa' ||
        usuario.role === 'admin')) ||
      (usuario.nivel && usuario.nivel !== 'usuario');
  }, [usuario]);


  const finalizarPedido = async () => {
    // Verificar se a loja está aberta
    if (!lojaAberta) {
      setShowModalLojaFechada(true);
      return;
    }

    // Verificar se há itens no carrinho
    if (!cart.hasItems) {
      toast.error('Adicione itens ao carrinho', { duration: 2000 });
      return;
    }

    // Meta Pixel: InitiateCheckout
    const contentIds = cart.cartData.itens.map(item => item.produto_id.toString());
    const totalValue = getTotalCarrinho();

    trackPixelEvent('InitiateCheckout', {
      content_ids: contentIds,
      value: totalValue,
      currency: 'BRL',
      num_items: cart.itemCount
    });

    // Fechar modais e redirecionar para a página dedicada de checkout
    fecharTodosModais();
    navigate(`/checkout/${empresaId || 1}`);
  };

  const handlePagamentoConfirmado = async (metodo: string, detalhes?: any) => {
    try {

      // Verificar se o usuário está autenticado
      if (!usuario || !usuario.id) {
        console.error('❌ Usuário não autenticado');
        toast.error('Você precisa estar logado para finalizar o pedido', { duration: 2000 });
        // Redirecionar para login
        navigate(`/login/${empresaId}`);
        return;
      }

      console.log('👤 Usuário autenticado:', {
        id: usuario.id,
        nome: usuario.nome,
        email: usuario.email,
        telefone: usuario.telefone
      });

      // Verificar se o endereço de entrega foi configurado (apenas se for entrega)
      if (tipoEntrega === 'entrega' && !enderecoEntrega) {
        toast.error('Configure o endereço de entrega', { duration: 2000 });
        return;
      }

      console.log('📍 Endereço de entrega:', enderecoEntrega);

      // Verificar dados do carrinho
      console.log('🛒 Dados do carrinho:', cart.cartData);
      console.log('📦 Itens no carrinho:', cart.cartData.itens);

      // Usar os dados do carrinho do servidor
      const itens = cart.cartData.itens.map(item => {
        console.log('🔍 Processando item:', item);

        const personalizacoes = item.personalizacoes
          .filter(p => p.opcao_id && p.item_id && p.opcao_id > 0 && p.item_id > 0) // Filtrar apenas personalizações válidas
          .map(p => ({
            opcao_id: p.opcao_id,
            item_id: p.item_id,
            nome: p.nome || '',
            tipo: 'adicionar',
            quantidade: p.quantidade || 1,
            preco_adicional: p.preco_adicional || 0
          }));

        console.log('🎨 Personalizações filtradas para item:', item.produto_id, personalizacoes);

        return {
          produto_id: item.produto_id,
          produto_nome: item.produto_nome,
          produto_descricao: item.descricao,
          quantidade: item.quantidade,
          preco_unitario: item.preco_unitario,
          observacoes: item.observacoes,
          personalizacoes
        };
      });

      // Log para debug
      console.log('🔍 Itens preparados para o pedido:', itens);
      console.log('🔍 Personalizações filtradas:', itens.map(item => item.personalizacoes));

      // Preparar dados do pedido com informações de entrega e pagamento
      const dadosPedido = {
        mesa: tipoEntrega === 'retirada' ? 'Retirada' : 'Entrega',
        tipo_pedido: tipoEntrega,
        itens,
        observacoes: cart.cartData.carrinho?.observacoes || '',
        dados_cliente: {
          nome: usuario?.nome?.split(' ')[0] || '',
          sobrenome: usuario?.nome?.split(' ').slice(1).join(' ') || '',
          telefone: usuario?.telefone || '',
          email: usuario?.email || '',
          endereco: tipoEntrega === 'entrega' ? enderecoEntrega : { retirada: true, loja: empresa?.nome }
        },
        valor_frete: temDireitoEntregaGratis() ? 0 : valorFrete,
        metodo_pagamento: metodo,
        // Adicionar detalhes específicos de pagamento
        detalhes_pagamento: detalhes,
        // Adicionar cupom se aplicado
        cupom_codigo: detalhes?.cupom?.codigo || null,
        cupom_id: detalhes?.cupom?.promocao_id || null,
        desconto_cupom: detalhes?.cupom?.desconto || 0
      };

      console.log('📋 Dados do pedido preparados:', dadosPedido);
      console.log('💰 Valor do frete:', valorFrete);
      console.log('💳 Método de pagamento final:', metodo);
      console.log('📋 Detalhes de pagamento:', detalhes);

      // Se o método for 'approved' (PIX aprovado) ou 'site', não criar novo pedido
      // pois o pedido já foi criado pelo MercadoPagoCheckout
      if (metodo === 'approved' || metodo === 'site') {
        console.log('✅ Pagamento pelo site - Pedido já foi criado pelo MercadoPagoCheckout');
        console.log('📋 Detalhes do pagamento:', detalhes);

        // O bot será notificado automaticamente pelo backend
        console.log('✅ Pagamento confirmado - o bot será notificado automaticamente pelo backend');

        // Meta Pixel: Purchase
        const contentIds = cart.cartData.itens.map(item => item.produto_id.toString());
        const totalValue = getTotalCarrinho();

        trackPixelEvent('Purchase', {
          content_ids: contentIds,
          value: totalValue,
          currency: 'BRL',
          num_items: cart.itemCount
        });

        // Limpar o carrinho e mostrar sucesso
        cart.clearCart();
        fecharTodosModais();

        if (metodo === 'approved') {
          toast.success('🎉 Pagamento PIX aprovado! Seu pedido está sendo preparado.', {
            duration: 2500
          });
        } else {
          toast.success('🎉 Pagamento confirmado! Seu pedido está sendo preparado.', {
            duration: 2500
          });
        }

        return;
      }

      // Enviar pedido para a API (apenas para outros métodos de pagamento)
      const response = await api.post('/api/pedidos', dadosPedido);

      // O bot será notificado automaticamente pelo backend
      console.log('✅ Pedido salvo - o bot será notificado automaticamente pelo backend');


      // Meta Pixel: Purchase
      const contentIds = cart.cartData.itens.map(item => item.produto_id.toString());
      const totalValue = getTotalCarrinho();

      trackPixelEvent('Purchase', {
        content_ids: contentIds,
        value: totalValue,
        currency: 'BRL',
        num_items: cart.itemCount
      });

      // Mensagem de sucesso baseada no método de pagamento
      if (metodo === 'entrega') {
        let mensagem = '🎉 Pedido realizado com sucesso! Pague na entrega.';

        // Adicionar detalhes específicos na mensagem
        if (detalhes?.metodo) {
          switch (detalhes.metodo) {
            case 'dinheiro':
              if (detalhes.mensagemTroco) {
                mensagem += ` 💰 ${detalhes.mensagemTroco}`;
              } else {
                mensagem += ` 💰 Forma: Dinheiro${detalhes.precisaTroco ? ` (Troco para R$ ${detalhes.valorTroco})` : ''}`;
              }
              break;
            case 'cartao':
              mensagem += ' 💳 Forma: Cartão (levar maquininha)';
              break;
            case 'pix':
              mensagem += ' 📱 Forma: PIX via celular';
              break;
          }
        }

        toast.success(mensagem, { duration: 2500 });
      } else if (metodo === 'site') {
        let mensagem = '🎉 Pedido realizado com sucesso! ';

        if (detalhes?.status === 'pago') {
          mensagem += '✅ Pagamento confirmado! Seu pedido está sendo preparado.';
        } else if (detalhes?.metodo) {
          switch (detalhes.metodo) {
            case 'pix_site':
              mensagem += '📱 Pague via PIX pelo site.';
              break;
            case 'cartao_site':
              mensagem += '💳 Pague com cartão pelo site.';
              break;
            case 'mercadopago':
              mensagem += '✅ Pagamento via Mercado Pago confirmado!';
              break;
          }
        }

        toast.success(mensagem, { duration: 2500 });
      } else if (tipoEntrega === 'retirada') {
        toast.success('🎉 Pedido para retirada realizado com sucesso! Aguarde a confirmação.', { duration: 2500 });
      }

      // Fechar modal de pagamento e limpar tudo
      await cart.clearCart();
      fecharTodosModais();


    } catch (error: any) {
      console.error('❌ ERRO ao finalizar pedido:', error);
      console.error('📋 Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });

      // Erro ao finalizar pedido
      if (error.response) {
        console.error('📡 Resposta do servidor:', error.response.data);
        console.error('🔢 Status HTTP:', error.response.status);
        console.error('📋 Headers da resposta:', error.response.headers);
      }

      // Log do erro completo
      console.error('🔍 Erro completo:', {
        name: error.name,
        code: error.code,
        message: error.message,
        stack: error.stack,
        config: error.config,
        isAxiosError: error.isAxiosError
      });

      toast.error('Erro ao finalizar pedido', { duration: 2000 });
    }
  };

  const chamarGarcom = async () => {
    if (!mesaGarcom.trim()) {
      toast.error('Digite o número da mesa', { duration: 2000 });
      return;
    }

    try {
      await api.post('/api/chamar-garcom', {
        mesa: mesaGarcom.trim(),
        empresa_id: empresaId
      });

      toast.success('Garçom chamado com sucesso!', { duration: 1500 });
      voltarModal();
      setMesaGarcom('');
    } catch (error) {
      toast.error('Erro ao chamar garçom', { duration: 2000 });
    }
  };



  const abrirModalCustomizacao = async (itemIndex: number) => {
    const item = cart.cartData.itens[itemIndex];
    if (!item) {
      console.log('Item não encontrado no carrinho para índice:', itemIndex);
      return;
    }

    console.log('Abrindo modal de customização para item:', item.produto_nome, 'índice:', itemIndex);

    // Carregar opções de personalização do produto
    try {
      const response = await api.get(`/api/menu/produto/${item.produto_id}/opcoes`);
      console.log('Opções carregadas da API:', response.data);
      setOpcoesCustomizacao(response.data);

      // Converter personalizações existentes para o formato do modal
      const personalizacoesConvertidas: { [key: number]: { itemId: number; quantidade: number }[] } = {};
      item.personalizacoes.forEach((p: any) => {
        if (!personalizacoesConvertidas[p.opcao_id]) {
          personalizacoesConvertidas[p.opcao_id] = [];
        }
        personalizacoesConvertidas[p.opcao_id].push({
          itemId: p.item_id,
          quantidade: p.quantidade
        });
      });

      console.log('Personalizações convertidas:', personalizacoesConvertidas);
      setPersonalizacoesCustomizacao(personalizacoesConvertidas);
      setObservacoesCustomizacao(item.observacoes || '');

      // Configurar modal após carregar dados
      setModalCustomizacao({
        show: true,
        itemIndex,
        item
      });
      // Trocar modal na pilha: sair de 'escolha-customizacao' e entrar em 'customizacao'
      voltarModal();
      abrirModal('customizacao');

    } catch (error) {
      console.error('Erro ao carregar opções de personalização:', error);
      setOpcoesCustomizacao([]);

      // Configurar modal mesmo sem opções
      setModalCustomizacao({
        show: true,
        itemIndex,
        item
      });
      voltarModal();
      abrirModal('customizacao');
    }
  };

  const handleOpcaoCustomizacaoChange = (opcaoId: number, itemId: number, checked: boolean) => {
    setPersonalizacoesCustomizacao(prev => {
      const current = prev[opcaoId] || [];
      let newSelection: { itemId: number; quantidade: number }[];

      if (checked) {
        newSelection = [...current, { itemId, quantidade: 1 }];
      } else {
        newSelection = current.filter(item => item.itemId !== itemId);
      }

      return {
        ...prev,
        [opcaoId]: newSelection
      };
    });
  };

  const handleQuantidadeCustomizacaoChange = (opcaoId: number, itemId: number, novaQuantidade: number) => {
    if (novaQuantidade < 1) return;

    setPersonalizacoesCustomizacao(prev => {
      const current = prev[opcaoId] || [];
      const newSelection = current.map(item =>
        item.itemId === itemId
          ? { ...item, quantidade: novaQuantidade }
          : item
      );

      return {
        ...prev,
        [opcaoId]: newSelection
      };
    });
  };

  const salvarCustomizacao = async () => {
    console.log('=== FUNÇÃO salvarCustomizacao CHAMADA ===');
    console.log('modalCustomizacao:', modalCustomizacao);

    if (modalCustomizacao.itemIndex === null || modalCustomizacao.itemIndex === undefined || !modalCustomizacao.item) {
      console.log('Erro: modalCustomizacao não está configurado corretamente');
      console.log('itemIndex:', modalCustomizacao.itemIndex, 'tipo:', typeof modalCustomizacao.itemIndex);
      return;
    }

    console.log('Salvando customização para item:', modalCustomizacao.itemIndex);
    console.log('Personalizações atuais:', personalizacoesCustomizacao);
    console.log('Opções disponíveis:', opcoesCustomizacao);

    // Preparar personalizações
    const personalizacoes: {
      opcao_id: number;
      item_id: number;
      nome: string;
      preco_adicional: number;
      quantidade: number;
      tipo: 'adicionar' | 'remover';
    }[] = [];

    Object.entries(personalizacoesCustomizacao).forEach(([opcaoId, items]) => {
      const opcao = opcoesCustomizacao.find(o => o.id === parseInt(opcaoId));
      if (!opcao) {
        console.log('Opção não encontrada para ID:', opcaoId);
        return;
      }

      items.forEach(item => {
        const opcaoItem = opcao.itens.find(i => i.id === item.itemId);
        if (!opcaoItem) {
          console.log('Item da opção não encontrado para ID:', item.itemId);
          return;
        }

        personalizacoes.push({
          opcao_id: parseInt(opcaoId),
          item_id: item.itemId,
          nome: opcaoItem.nome,
          preco_adicional: opcaoItem.preco_adicional,
          quantidade: item.quantidade,
          tipo: opcao.tipo
        });
      });
    });

    console.log('Personalizações preparadas:', personalizacoes);

    // Atualizar no carrinho usando o hook useCart
    const success = await cart.updateItem(
      modalCustomizacao.item.id,
      modalCustomizacao.item.quantidade,
      observacoesCustomizacao,
      personalizacoes
    );

    if (success) {
      console.log('Item atualizado com sucesso no carrinho');

      // Fechar modal de customização e voltar ao carrinho
      voltarModal();

      // Limpar estados de customização
      setPersonalizacoesCustomizacao({});
      setObservacoesCustomizacao('');

      toast.success('Lanches customizados com sucesso!', { duration: 1500 });
    } else {
      console.error('Falha ao atualizar o item no carrinho');
    }
  };

  // Função para aplicar máscara de telefone
  const formatarTelefone = (valor: string) => {
    // Remove tudo que não é número
    const numeros = valor.replace(/\D/g, '');

    // Aplica a máscara (11) 99999-9999
    if (numeros.length <= 2) {
      return numeros;
    } else if (numeros.length <= 7) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    } else {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
    }
  };

  // Função para aplicar máscara de CEP
  const formatarCEP = (valor: string) => {
    // Remove tudo que não é número
    const numeros = valor.replace(/\D/g, '');

    // Aplica a máscara 00000-000
    if (numeros.length <= 5) {
      return numeros;
    } else {
      return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
    }
  };

  // Função para lidar com mudanças nos campos do perfil
  const handlePerfilChange = (campo: string, valor: string) => {
    let valorFormatado = valor;

    // Aplicar máscaras específicas
    if (campo === 'telefone') {
      valorFormatado = formatarTelefone(valor);
    } else if (campo === 'cep') {
      valorFormatado = formatarCEP(valor);
    }

    setDadosPerfil(prev => ({
      ...prev,
      [campo]: valorFormatado
    }));
  };

  // Funções para o modal de perfil
  const carregarDadosPerfil = async () => {
    if (usuario) {
      try {
        // Carregar dados atualizados do perfil do servidor
        const response = await api.get('/api/auth/perfil');
        const { usuario: usuarioAtualizado } = response.data;

        console.log('📋 Dados atualizados do perfil:', usuarioAtualizado);

        const nomeCompleto = usuarioAtualizado.nome.split(' ');
        const nome = nomeCompleto[0] || '';
        const sobrenome = nomeCompleto.slice(1).join(' ') || '';

        // Usar dados do servidor (mais confiáveis)
        setDadosPerfil({
          nome,
          sobrenome,
          telefone: usuarioAtualizado.telefone || '',
          rua: usuarioAtualizado.rua || '',
          bairro: usuarioAtualizado.bairro || '',
          numero_casa: usuarioAtualizado.numero_casa || '',
          cep: usuarioAtualizado.cep || '',
          cidade: usuarioAtualizado.cidade || ''
        });

        console.log('✅ Dados do perfil carregados do servidor:', {
          nome,
          sobrenome,
          telefone: usuarioAtualizado.telefone,
          rua: usuarioAtualizado.rua,
          bairro: usuarioAtualizado.bairro,
          numero_casa: usuarioAtualizado.numero_casa,
          cep: usuarioAtualizado.cep,
          cidade: usuarioAtualizado.cidade
        });

      } catch (error) {
        console.error('❌ Erro ao carregar dados do perfil:', error);

        // Fallback para dados locais se der erro
        const nomeCompleto = usuario.nome.split(' ');
        const nome = nomeCompleto[0] || '';
        const sobrenome = nomeCompleto.slice(1).join(' ') || '';

        setDadosPerfil({
          nome,
          sobrenome,
          telefone: usuario.telefone || '',
          rua: usuario.rua || usuario.endereco?.rua || '',
          bairro: usuario.bairro || usuario.endereco?.bairro || '',
          numero_casa: usuario.numero_casa || usuario.endereco?.numero_casa || '',
          cep: usuario.cep || usuario.endereco?.cep || '',
          cidade: usuario.cidade || usuario.endereco?.cidade || ''
        });
      }
    }
  };

  // Carregar dados do perfil quando o modal abrir
  useEffect(() => {
    if (showPerfilModal && usuario) {
      carregarDadosPerfil();
    }
  }, [showPerfilModal, usuario]);

  const salvarPerfil = async () => {
    try {
      await atualizarPerfil(dadosPerfil);
      toast.success('Perfil atualizado com sucesso!', { duration: 1500 });
      setEditandoPerfil(false);


      // Recarregar dados do usuário
      await carregarPerfil();
    } catch (error: any) {
      toast.error('Erro ao atualizar perfil', { duration: 2000 });
    }
  };

  const resetarPerfil = () => {
    setEditandoPerfil(false);
  };

  const handleLogout = () => {
    logout();
    fecharTodosModais();
    resetarPerfil();
    toast.success('Logout realizado com sucesso!', { duration: 1500 });
  };

  const handleExcluirConta = () => {
    abrirModal('excluir-conta');
  };

  const confirmarExclusaoConta = async () => {
    try {
      await api.delete('/api/auth/conta');
      toast.success('Conta excluída com sucesso!', { duration: 2000 });
      logout();
      fecharTodosModais();
    } catch (error: any) {
      console.error('Erro ao excluir conta:', error);
      const mensagemErro = error.response?.data?.erro || 'Erro ao excluir conta';
      toast.error(mensagemErro);
    }
  };

  const cancelarExclusaoConta = () => {
    voltarModal(); // volta para o modal de perfil
  };

  // Função para abrir WhatsApp
  const abrirWhatsApp = () => {
    const mensagem = `Olá! Gostaria de fazer um pedido ou tirar alguma dúvida sobre o menu.`;
    const urlWhatsApp = `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(mensagem)}`;

    const novaJanela = window.open(urlWhatsApp, '_blank');
    if (!novaJanela) {
      toast.error('Não foi possível abrir o WhatsApp. Verifique se o navegador permite pop-ups.', { duration: 2000 });
      return;
    }

    toast.success('Redirecionando para o WhatsApp...', { duration: 1500 });
  };

  // Função para limpar carrinho - usando hook
  const limparCarrinho = async () => {
    await cart.clearCart();
    setShowCarrinho(false);
    setValorFrete(0);
    setEnderecoEntrega(null);
    setTipoEntrega('entrega'); // Resetar para entrega
    toast.success('Carrinho limpo com sucesso!', { duration: 1500 });
  };


  // Funções para gerenciar endereço e frete (estabilizadas com useCallback)
  const handleEnderecoChange = useCallback((endereco: Endereco | null) => {
    setEnderecoEntrega(endereco);
  }, []);

  const handleFreteChange = useCallback((valor: number) => {
    // Só definir o valor do frete se não for entrega grátis
    if (!temDireitoEntregaGratis()) {
      setValorFrete(valor);
    } else {
      // Para entrega grátis, sempre manter como 0
      setValorFrete(0);
    }
  }, [temDireitoEntregaGratis]);

  // Garantir que o frete seja 0 quando entrega grátis for ativada
  useEffect(() => {
    if (tipoEntrega === 'retirada') {
      setValorFrete(0);
    } else if (temDireitoEntregaGratis()) {
      console.log('🎁 Entrega grátis ativada - definindo frete como 0');
      setValorFrete(0);
    }
  }, [cart.cartData.entregaGratis, cart.cartData.total, tipoEntrega]);

  // Carregar dados do perfil quando usuário estiver disponível
  useEffect(() => {
    if (usuario) {
      // Separar nome e sobrenome
      const nomeCompleto = usuario.nome || '';
      const partesNome = nomeCompleto.split(' ');
      const nome = partesNome[0] || '';
      const sobrenome = partesNome.slice(1).join(' ') || '';

      const dadosPerfilAtualizados = {
        nome,
        sobrenome,
        telefone: usuario.telefone || '',
        rua: usuario.rua || usuario.endereco?.rua || '',
        bairro: usuario.bairro || usuario.endereco?.bairro || '',
        numero_casa: usuario.numero_casa || usuario.endereco?.numero_casa || '',
        cep: usuario.cep || usuario.endereco?.cep || '',
        cidade: usuario.cidade || usuario.endereco?.cidade || ''
      };

      setDadosPerfil(prev => {
        if (JSON.stringify(prev) === JSON.stringify(dadosPerfilAtualizados)) {
          return prev;
        }
        return dadosPerfilAtualizados;
      });
    }
  }, [usuario?.id]);


  // Monitorar tamanho da tela
  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };

    // Definir largura inicial
    updateScreenWidth();

    // Adicionar listener para mudanças de tamanho
    window.addEventListener('resize', updateScreenWidth);

    return () => {
      window.removeEventListener('resize', updateScreenWidth);
    };
  }, []);

  // Inicializar produtos visíveis por categoria quando o tamanho da tela muda
  useEffect(() => {
    if (screenWidth > 0) {
      const produtosIniciais = getProdutosIniciais();
      console.log('🔄 Inicializando produtos visíveis:', {
        screenWidth,
        produtosIniciais,
        categorias: categorias.map(c => c.nome)
      });

      // Inicializar todas as categorias com o número correto de produtos
      const novosProdutosVisiveis: { [key: string]: number } = {};
      categorias.forEach(categoria => {
        novosProdutosVisiveis[categoria.nome] = produtosIniciais;
      });

      setProdutosVisiveisPorCategoria(novosProdutosVisiveis);
    }
  }, [screenWidth, categorias]);

  const mobileTipShownRef = useRef(false);
  const mobileMenuTipWidthStyle = useMemo(() => ({ width: 'min(96vw, 360px)' }), []);
  const mobileMenuTipDuration = 3200;

  // Mostrar card de dica do menu mobile quando a página carrega (apenas no mobile)
  useEffect(() => {
    if (!isMobile || mobileTipShownRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      mobileTipShownRef.current = true;
      setShowMobileMenuTip(true);
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [isMobile]);

  useEffect(() => {
    if (!showMobileMenuTip) {
      return;
    }

    const autoHideTimer = window.setTimeout(() => {
      setShowMobileMenuTip(false);
    }, mobileMenuTipDuration);

    return () => window.clearTimeout(autoHideTimer);
  }, [showMobileMenuTip]);

  // Garantir scroll livre e fluido no mobile
  useEffect(() => {
    if (deviceType === 'mobile') {
      document.body.style.overflowX = 'hidden';
      document.body.style.overflowY = 'auto';
      document.documentElement.style.overflowX = 'hidden';
      document.documentElement.style.overflowY = 'auto';

      return () => {
        document.body.style.overflowX = '';
        document.body.style.overflowY = '';
        document.documentElement.style.overflowX = '';
        document.documentElement.style.overflowY = '';
      };
    }
  }, [deviceType]);

  // Função para retornar ícone profissional da biblioteca lucide-react para cada categoria
  const getCategoriaIcon = (nomeCategoria: string) => {
    const nome = (nomeCategoria || '').toLowerCase();
    if (nome.includes('pão') || nome.includes('paes') || nome.includes('salgado') || nome.includes('baguete')) {
      return <Wheat className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('doce') || nome.includes('bolo') || nome.includes('alfajor') || nome.includes('torta')) {
      return <Cookie className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('quente') || nome.includes('café') || nome.includes('cafe') || nome.includes('chá')) {
      return <Coffee className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('gelad') || nome.includes('bebida') || nome.includes('refri') || nome.includes('suco') || nome.includes('agua') || nome.includes('água')) {
      return <CupSoda className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('prato') || nome.includes('alaminuta') || nome.includes('almoço') || nome.includes('refeic')) {
      return <UtensilsCrossed className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('porç') || nome.includes('porcao') || nome.includes('acompanha') || nome.includes('petisco')) {
      return <Soup className="w-4 h-4 text-[#b85836]" />;
    }
    if (nome.includes('pizza')) {
      return <Pizza className="w-4 h-4 text-[#b85836]" />;
    }
    return <ShoppingBag className="w-4 h-4 text-[#b85836]" />;
  };

  const handleHeaderClick = useCallback(async () => {
    if (!empresaId) return;

    try {
      await Promise.all([
        cacheService.invalidate(`menu_${empresaId}`),
        cacheService.invalidate(`empresa_${empresaId}`),
        // Limpar qualquer outro dado que possa estar cacheado
        localStorage.removeItem(`menu_${empresaId}_timestamp`),
        localStorage.removeItem(`empresa_${empresaId}_timestamp`)
      ]);
    } catch (error) {
      console.error('❌ Não foi possível invalidar cache do menu ao clicar no título:', error);
    }

    try {
      await Promise.all([
        refetchMenu(),
        refetchEmpresa(),
        checkHorarioFuncionamento() // Re-verificar status imediatamente
      ]);
    } catch (error) {
      console.error('❌ Erro ao refazer fetch do menu após clicar no título:', error);
    }

    navigate(`/menu/${empresaId || ''}`);
  }, [empresaId, refetchMenu, refetchEmpresa, checkHorarioFuncionamento, navigate]);

  // URL da Logo da Empresa com suporte a links absolutos ou relativos
  const urlLogoEmpresa = useMemo(() => {
    if (!empresa?.logo_url || typeof empresa.logo_url !== 'string' || empresa.logo_url.trim().length === 0) {
      return null;
    }
    return empresa.logo_url.startsWith('http')
      ? empresa.logo_url
      : `${config.api.baseUrl}${empresa.logo_url.startsWith('/') ? '' : '/'}${empresa.logo_url}`;
  }, [empresa?.logo_url]);

  // Função para renderizar a imagem do produto ou a logo da loja como fallback
  const renderImagemProduto = (produto: Produto, isLista: boolean = false) => {
    const imagemValida = produto.imagem_url && typeof produto.imagem_url === 'string' && produto.imagem_url.trim().length > 0;
    const urlImagemProduto = imagemValida
      ? (produto.imagem_url.startsWith('http')
          ? produto.imagem_url
          : `${config.api.baseUrl}${produto.imagem_url.startsWith('/') ? '' : '/'}${produto.imagem_url}`)
      : null;

    // Renderiza o conteúdo do fallback (se não tiver imagem ou der erro)
    const renderFallbackContent = (tamanho: 'lista' | 'grade') => {
      if (urlLogoEmpresa) {
        return (
          <div className="w-full h-full flex flex-col items-center justify-center p-2 bg-gradient-to-br from-slate-900 via-[#131b2e] to-slate-950">
            <div className={`rounded-xl overflow-hidden border border-white/10 p-1.5 shadow-md bg-slate-900/90 flex items-center justify-center ${tamanho === 'lista' ? 'w-12 h-12' : 'w-16 h-16'}`}>
              <img
                src={urlLogoEmpresa}
                alt={empresa?.nome || 'Logo'}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        );
      }

      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-3 bg-gradient-to-br from-slate-900 via-[#131b2e] to-slate-950">
          <div className={`rounded-2xl flex items-center justify-center shadow-lg bg-orange-500/15 text-orange-400 border border-orange-500/30 ${tamanho === 'lista' ? 'w-10 h-10' : 'w-14 h-14'}`}>
            <UtensilsCrossed className={tamanho === 'lista' ? 'w-5 h-5' : 'w-7 h-7'} />
          </div>
        </div>
      );
    };

    if (isLista) {
      return (
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden shadow-xs flex-shrink-0 flex items-center justify-center border border-white/10 bg-[#090d16] relative">
          {urlImagemProduto ? (
            <>
              <img
                src={urlImagemProduto}
                alt={produto.nome}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="w-full h-full" style={{ display: 'none' }}>
                {renderFallbackContent('lista')}
              </div>
            </>
          ) : (
            renderFallbackContent('lista')
          )}
        </div>
      );
    }

    // Para visualização em Grade (Mobile e Desktop)
    return (
      <div className="w-full h-28 sm:h-36 md:h-40 rounded-xl mb-2 overflow-hidden shadow-xs relative flex items-center justify-center border border-white/10 bg-[#090d16]">
        {urlImagemProduto ? (
          <>
            <img
              src={urlImagemProduto}
              alt={produto.nome}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="w-full h-full" style={{ display: 'none' }}>
              {renderFallbackContent('grade')}
            </div>
          </>
        ) : (
          renderFallbackContent('grade')
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#F6F7F9] flex items-center justify-center text-gray-800">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="h-14 w-14 border-4 border-t-[#fcc3ac] border-gray-200 rounded-full animate-spin shadow-lg"></div>
          <p className="text-sm tracking-[0.2em] uppercase text-gray-600 font-bold">Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-theme-container
      className={`min-h-screen ${activeTheme.isDark ? 'theme-mode-dark' : 'theme-mode-light'} text-gray-900 ${deviceType === 'mobile' ? 'overflow-y-auto overflow-x-hidden' : 'lg:overflow-x-hidden lg:overflow-y-visible overflow-hidden'}`}
      style={{
        '--bg-app': activeTheme.colors.bgApp,
        '--bg-card': activeTheme.colors.bgCard,
        '--border-card': activeTheme.colors.borderCard,
        '--bg-header': activeTheme.colors.bgHeader,
        '--border-header': activeTheme.colors.borderHeader,
        '--text-primary': activeTheme.colors.textPrimary,
        '--text-muted': activeTheme.colors.textMuted,
        '--color-primary': activeTheme.colors.primary,
        '--color-primary-hover': activeTheme.colors.primaryHover,
        '--color-primary-text': activeTheme.colors.primaryText,
        '--color-primary-border': activeTheme.colors.primaryBorder,
        '--color-primary-subtle': activeTheme.colors.primarySubtle,
        '--color-primary-subtle-text': activeTheme.colors.primarySubtleText,
        '--color-accent': activeTheme.colors.accent,
        '--color-price': activeTheme.colors.price,
        '--bg-surface-elevated': activeTheme.colors.bgSurfaceElevated || activeTheme.colors.bgCard,
        backgroundColor: activeTheme.colors.bgApp,
        color: activeTheme.colors.textPrimary,
      } as React.CSSProperties}
    >
      {/* Header Fixo - Versões isoladas por dispositivo */}
      <div
        data-header-container
        className="bg-white/95 backdrop-blur-md shadow-sm fixed top-0 left-0 right-0 z-[1000] border-b border-gray-200/80"
      >

        {/* HEADER MOBILE */}
        {deviceType === 'mobile' && (
          <div className="max-w-7xl mx-auto px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              {/* Lado Esquerdo: Logo da Loja + Nome da Empresa e Status */}
              <div
                className="flex items-center gap-2 min-w-0 cursor-pointer"
                onClick={handleHeaderClick}
              >
                {urlLogoEmpresa && (
                  <div className={`w-8 h-8 rounded-lg overflow-hidden border p-0.5 shadow-xs flex-shrink-0 flex items-center justify-center ${activeTheme.isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200/80'}`}>
                    <img
                      src={urlLogoEmpresa}
                      alt={empresa?.nome || 'Logo'}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-black text-gray-900 truncate max-w-[170px] tracking-tight">
                    {empresa?.nome || 'DK System'}
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={`w-2 h-2 rounded-full ${lojaAberta ? 'bg-[#00B368] animate-pulse' : 'bg-red-500'}`}></div>
                    <span className={`text-[10px] font-bold ${lojaAberta ? 'text-emerald-700' : 'text-red-600'}`}>
                      {statusLojaCarregando ? 'Carregando...' : lojaAberta ? 'Loja Aberta' : 'Loja Fechada'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Lado Direito: Ações Compactas */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Busca */}
                <button
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className={`p-2 rounded-xl transition-colors ${showMobileSearch ? 'bg-[#fcc3ac]/40 text-[#4a1c0d]' : 'text-gray-700 hover:bg-gray-100'}`}
                  aria-label="Buscar produtos"
                >
                  <Search className="h-5 w-5" />
                </button>

                {/* Menu de Categorias */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className={`p-2 rounded-xl transition-colors ${showMobileMenu ? 'bg-[#fcc3ac]/40 text-[#4a1c0d]' : 'text-gray-700 hover:bg-gray-100'}`}
                  aria-label="Menu de categorias"
                >
                  <Menu className="h-5 w-5" />
                </button>

                {/* Perfil */}
                <button
                  onClick={() => usuario ? setShowPerfilModal(true) : navigate('/login', { state: { from: { pathname: `/menu/${empresaId}` } } })}
                  className="p-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  aria-label="Perfil do usuário"
                >
                  <User className="h-5 w-5" />
                </button>

                {/* Carrinho em Laranja Flame */}
                <button
                  onClick={() => setShowCarrinho(!showCarrinho)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold border border-orange-400/40 p-2 rounded-xl relative shadow-md shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center"
                  aria-label="Carrinho de compras"
                >
                  <ShoppingCart className="h-5 w-5 text-white" />
                  {cart.isLoading ? (
                    <div className="absolute -top-1 -right-1 bg-slate-900 text-orange-400 rounded-full h-4 w-4 flex items-center justify-center shadow-md">
                      <div className="animate-spin rounded-full h-2 w-2 border border-orange-500 border-t-transparent"></div>
                    </div>
                  ) : cart.hasItems && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-black rounded-full h-4 w-4 flex items-center justify-center shadow-md animate-bounce">
                      {cart.itemCount > 9 ? '9+' : cart.itemCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Gaveta de Busca Mobile (quando ativada) */}
            {showMobileSearch && (
              <div className="pt-2 pb-1 animate-fade-in">
                <div className="relative">
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="O que você deseja comer hoje?"
                    className="w-full pl-9 pr-8 py-2 text-xs bg-gray-100 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#fcc3ac] text-gray-900"
                    autoFocus
                  />
                  <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-400" />
                  {busca && (
                    <button
                      onClick={() => setBusca('')}
                      className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* HEADER TABLET (iPad) - Layout Horizontal Compacto */}
        {deviceType === 'tablet' && (
          <div className="max-w-7xl mx-auto px-6 py-2.5">
            <div className="flex items-center justify-between gap-4">
              {/* Lado Esquerdo: Logo + Nome da empresa e Status */}
              <div
                className="flex items-center gap-3 cursor-pointer min-w-0"
                onClick={handleHeaderClick}
              >
                {urlLogoEmpresa && (
                  <div className={`w-10 h-10 rounded-xl overflow-hidden border p-1 shadow-xs flex-shrink-0 flex items-center justify-center ${activeTheme.isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200/80'}`}>
                    <img
                      src={urlLogoEmpresa}
                      alt={empresa?.nome || 'Logo'}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <span className="text-lg font-black text-gray-900 truncate max-w-[280px] tracking-tight">
                  {empresa?.nome || 'DK System'}
                </span>
                <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200/80 rounded-full px-2.5 py-0.5">
                  <div className={`w-2 h-2 rounded-full ${lojaAberta ? 'bg-[#00B368] animate-pulse' : 'bg-red-500'}`}></div>
                  <span className={`text-xs font-bold ${lojaAberta ? 'text-emerald-700' : 'text-red-600'}`}>
                    {statusLojaCarregando ? 'Carregando...' : lojaAberta ? 'Loja Aberta' : 'Loja Fechada'}
                  </span>
                </div>
              </div>

              {/* Lado Direito: Ações */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Busca */}
                <button
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className={`p-2 rounded-xl transition-colors ${showMobileSearch ? 'bg-[#fcc3ac]/40 text-[#4a1c0d]' : 'text-gray-700 hover:bg-gray-100'}`}
                  aria-label="Buscar produtos"
                >
                  <Search className="h-5 w-5" />
                </button>

                {/* Menu de Categorias */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className={`p-2 rounded-xl transition-colors ${showMobileMenu ? 'bg-[#fcc3ac]/40 text-[#4a1c0d]' : 'text-gray-700 hover:bg-gray-100'}`}
                  aria-label="Menu de categorias"
                >
                  <Menu className="h-5 w-5" />
                </button>

                {/* Dashboard (se aplicável) */}
                {usuario && temCargo && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="p-2 text-gray-700 hover:text-[#b85836] hover:bg-gray-100 rounded-xl transition-colors"
                    title="Ir para Dashboard"
                    aria-label="Dashboard"
                  >
                    <LayoutDashboard className="h-5 w-5" />
                  </button>
                )}

                {/* Perfil */}
                {usuario ? (
                  <button
                    onClick={() => setShowPerfilModal(true)}
                    className="flex items-center gap-1.5 p-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                    aria-label="Perfil do usuário"
                  >
                    <User className="h-5 w-5" />
                    <span className="text-xs font-bold text-gray-800">
                      {usuario.nome.split(' ')[0]}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/login', { state: { from: { pathname: `/menu/${empresaId}` } } })}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                    aria-label="Fazer login"
                  >
                    <User className="h-5 w-5" />
                  </button>
                )}

                {/* Carrinho em Laranja Flame */}
                <button
                  onClick={() => setShowCarrinho(!showCarrinho)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold border border-orange-400/40 p-2.5 rounded-xl relative shadow-md shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center"
                  aria-label="Carrinho de compras"
                >
                  <ShoppingCart className="h-5 w-5 text-white" />
                  {cart.isLoading ? (
                    <div className="absolute -top-1 -right-1 bg-slate-900 text-orange-400 rounded-full h-4 w-4 flex items-center justify-center shadow-md">
                      <div className="animate-spin rounded-full h-2 w-2 border border-orange-500 border-t-transparent"></div>
                    </div>
                  ) : cart.hasItems && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-black rounded-full h-4 w-4 flex items-center justify-center shadow-md animate-bounce">
                      {cart.itemCount > 9 ? '9+' : cart.itemCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HEADER DESKTOP - Layout Horizontal Elegante iFood/99 Food */}
        {deviceType === 'desktop' && (
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between py-2.5 gap-6">
              {/* Lado Esquerdo: Logo + Nome da empresa + Status da loja */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div
                  className="cursor-pointer group flex items-center gap-2.5"
                  onClick={() => navigate(`/menu/${empresaId || ''}`)}
                >
                  {urlLogoEmpresa && (
                    <div className={`w-10 h-10 rounded-xl overflow-hidden border p-1 shadow-xs flex items-center justify-center group-hover:scale-105 transition-transform duration-200 flex-shrink-0 ${activeTheme.isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200/80'}`}>
                      <img
                        src={urlLogoEmpresa}
                        alt={empresa?.nome || 'Logo'}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <span className="text-xl xl:text-2xl font-black tracking-tight text-gray-900 group-hover:text-[#b85836] transition-colors whitespace-nowrap">
                    {empresa?.nome || 'DK System'}
                  </span>
                </div>

                {/* Status da Loja inline */}
                {statusLojaCarregando ? (
                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200/80 rounded-full px-2.5 py-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-emerald-700 font-bold">Loja Aberta</span>
                  </div>
                ) : lojaAberta ? (
                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200/80 rounded-full px-2.5 py-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-emerald-700 font-bold">Loja Aberta</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-rose-50 border border-rose-200/80 rounded-full px-2.5 py-1">
                    <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-rose-700 font-bold">Loja Fechada</span>
                  </div>
                )}
              </div>

              {/* Centro: Barra de Pesquisa Desktop Integrada */}
              <div className="flex-1 max-w-lg mx-auto">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    className="w-full pl-10 pr-9 py-2 bg-gray-100/80 hover:bg-white focus:bg-white border border-gray-200/80 focus:border-[#fcc3ac] focus:ring-2 focus:ring-[#fcc3ac]/20 rounded-full text-xs text-gray-900 placeholder-gray-400 transition-all shadow-inner"
                    placeholder="Buscar no cardápio..."
                  />
                  {busca && (
                    <button
                      onClick={() => setBusca('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-700 transition-colors"
                      aria-label="Limpar busca"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Lado Direito: Perfil & Carrinho */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Botão Dashboard - apenas para usuários com cargo */}
                {usuario && temCargo && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-gray-600 hover:text-[#b85836] transition-colors p-2 hover:bg-gray-100 rounded-full"
                    title="Ir para Dashboard"
                    aria-label="Dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                  </button>
                )}

                {/* Perfil */}
                {usuario ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-700 font-semibold hidden xl:inline">
                      Olá, {usuario.nome.split(' ')[0]}
                    </span>
                    <button
                      onClick={() => setShowPerfilModal(true)}
                      className="text-gray-700 hover:text-[#b85836] transition-colors p-2 hover:bg-gray-100 rounded-full"
                      aria-label="Perfil do usuário"
                    >
                      <User className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/login', { state: { from: { pathname: `/menu/${empresaId}` } } })}
                    className="text-gray-700 hover:text-[#b85836] transition-colors px-3 py-1.5 hover:bg-gray-100 rounded-full font-semibold text-xs flex items-center gap-1.5"
                    title="Login de Cliente"
                  >
                    <User className="h-4 w-4" />
                    <span>Entrar</span>
                  </button>
                )}

                {/* Carrinho em Laranja Flame */}
                <button
                  onClick={() => {
                    console.log('🛒 Clicou no carrinho - showCarrinho:', showCarrinho);
                    setShowCarrinho(!showCarrinho);
                  }}
                  className="flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border border-orange-400/40 rounded-full font-bold text-xs transition-all shadow-md shadow-orange-500/20 active:scale-95"
                  aria-label="Carrinho de compras"
                >
                  <ShoppingCart className="h-4 w-4 text-white" />
                  <span className="text-white font-bold">Carrinho</span>
                  {cart.isLoading ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : cart.hasItems ? (
                    <span className="bg-red-600 text-white rounded-full px-1.5 py-0.2 text-[10px] font-black shadow-xs">
                      {cart.itemCount > 9 ? '9+' : cart.itemCount}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Barra de Pesquisa Mobile/Tablet */}
      {showMobileSearch && (
        <div
          className={`${deviceType === 'desktop' ? 'hidden' : 'fixed'} left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200/80 p-4 shadow-md animate-fade-in`}
          style={{
            top: `${headerHeight}px`
          }}
        >
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className={`${deviceType === 'mobile' ? 'h-5 w-5' : 'h-6 w-6'} text-gray-400`} />
            </div>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className={`w-full pl-10 pr-12 ${deviceType === 'mobile' ? 'py-3' : 'py-3.5'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#fcc3ac] focus:border-transparent transition-all duration-300 shadow-inner`}
              placeholder="Buscar produtos..."
              autoFocus
            />
            <button
              onClick={() => setShowMobileSearch(false)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X className={`${deviceType === 'mobile' ? 'h-5 w-5' : 'h-6 w-6'}`} />
            </button>
          </div>
        </div>
      )}

      {/* Container Principal com Layout Fixo - Ajustado por dispositivo */}
      <div
        className={`${deviceType === 'mobile' ? 'block' : 'flex'} min-h-[calc(100vh-var(--header-height))]`}
        style={{
          paddingTop: showMobileSearch
            ? `${headerHeight + 70}px`
            : `${headerHeight}px`,
          '--header-height': `${headerHeight}px`
        } as React.CSSProperties}
      >
        {/* Overlay para fechar menu mobile */}
        {showMobileMenu && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
          />
        )}


      {/* Sidebar Fixa - Categorias */}
        <div className={`lg:w-64 bg-white border-r border-gray-200/80 transition-all duration-300 fixed lg:sticky ${showMobileMenu ? 'z-[1100]' : 'z-30'
          } lg:z-[40] ${showMobileMenu ? 'block' : 'hidden lg:block'
          }`} style={deviceType === 'desktop' ? { top: `${headerHeight}px`, maxHeight: `calc(100vh - ${headerHeight}px)`, overflowY: 'auto' } : {
            top: showMobileMenu
              ? '0px'
              : `${headerHeight}px`
          }}>
          {/* Container principal do sidebar */}
          <div className="h-screen lg:h-full flex flex-col bg-white" style={{
            height: showMobileMenu
              ? '100vh'
              : `calc(100vh - ${headerHeight}px)`
          }}>
            {/* Header Mobile */}
            <div className="flex lg:hidden justify-between items-center p-4 border-b border-gray-100" style={{ paddingTop: showMobileMenu ? 'max(16px, env(safe-area-inset-top))' : '16px' }}>
              <h2 className="text-lg font-bold text-gray-900">
                Categorias
              </h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="text-gray-500 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-xl"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Conteúdo do sidebar com scroll */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-3">
              {/* Título Desktop */}
              <h2 className="hidden lg:block text-base font-extrabold text-gray-900 mb-4 text-center">
                Categorias
              </h2>

              {/* Botão Ver Tudo */}
              <button
                onClick={toggleVerTudo}
                className={`w-full py-2.5 px-3 rounded-xl text-left transition-all duration-200 flex items-center justify-between mb-3 ${mostrarTodos
                  ? 'bg-[#fcc3ac] text-[#4a1c0d] font-bold shadow-xs border border-[#f5b298]/60'
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 border border-gray-200/60 font-semibold'
                  }`}
              >
                <div className="flex items-center gap-2">
                  {mostrarTodos ? (
                    <>
                      <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-bold">Categorias</span>
                    </>
                  ) : (
                    <>
                      <LayoutGrid className={`w-4 h-4 flex-shrink-0 ${mostrarTodos ? 'text-white' : 'text-[#b85836]'}`} />
                      <span className="text-sm font-bold">Ver Todos</span>
                    </>
                  )}
                </div>
                {!mostrarTodos && (
                  <span className="text-[10px] bg-white text-gray-500 px-2 py-0.5 rounded-full border border-gray-200 font-medium">
                    Todos
                  </span>
                )}
              </button>

              {/* Lista de categorias */}
              <div className="space-y-1.5 mb-6">
                {categorias.map((categoria) => {
                  const isAtiva = categoriaFiltro === categoria.nome;
                  return (
                    <button
                      key={categoria.nome}
                      onClick={() => {
                        setCategoriaFiltro(isAtiva ? '' : categoria.nome);
                        setMostrarTodos(false);
                        setBusca('');
                        setShowMobileMenu(false);
                        setShowMobileSearch(false);
                      }}
                      className={`w-full py-2.5 px-3 rounded-xl text-left transition-all duration-200 flex items-center justify-between group ${isAtiva
                        ? 'bg-[#fcc3ac] text-[#4a1c0d] font-bold shadow-xs border border-[#f5b298]/60'
                        : 'bg-white hover:bg-orange-50/60 text-gray-700 hover:text-[#b85836] border border-gray-100 hover:border-orange-200 font-medium'
                        }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {categoria.imagem_url ? (
                          <img
                            src={categoria.imagem_url}
                            alt={categoria.nome}
                            className="w-6 h-6 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${isAtiva ? 'bg-white/20 text-white' : 'bg-[#fcc3ac]/40 text-[#4a1c0d]'}`}>
                            {getCategoriaIcon(categoria.nome)}
                          </div>
                        )}
                        <span className="text-sm truncate font-medium">
                          {capitalizarPrimeiraLetra(categoria.nome)}
                        </span>
                      </div>
                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isAtiva ? 'text-white translate-x-0.5' : 'text-gray-300 group-hover:text-[#b85836]'}`} />
                    </button>
                  );
                })}
              </div>

              {/* Rodapé do Sidebar */}
              <div className="text-center pt-2 pb-1">
                <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-900/60 border border-white/5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shadow-[0_0_6px_rgba(245,158,11,0.8)]" />
                    <span className="text-[10px] text-slate-400 font-medium">Desenvolvido por</span>
                  </div>
                  <span className="text-xs font-black tracking-wider bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent">
                    DK SYSTEMS
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal com Scroll */}
        <div className={`${deviceType === 'mobile' ? 'w-full' : 'flex-1'} bg-[var(--bg-app,#0b0f17)] overflow-x-hidden ${deviceType === 'mobile' ? '' : 'lg:overflow-visible overflow-y-auto'}`}>
          <div className="p-3 sm:p-6 lg:p-5 max-w-full">

            {/* Botões de Alternância de Visualização - Mobile/iPad */}
            <div className="lg:hidden mb-6">
              <div className="flex justify-center">
                <div className="bg-slate-900/90 rounded-xl p-1 border border-white/10 shadow-sm">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setVisualizacaoMobile('lista')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${visualizacaoMobile === 'lista'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold shadow-md'
                        : 'text-slate-300 hover:text-white hover:bg-white/10 font-medium'
                        }`}
                    >
                      <List className="h-4 w-4" />
                      <span className="text-sm font-medium">Lista</span>
                    </button>
                    <button
                      onClick={() => setVisualizacaoMobile('grade')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${visualizacaoMobile === 'grade'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold shadow-md'
                        : 'text-slate-300 hover:text-white hover:bg-white/10 font-medium'
                        }`}
                    >
                      <Grid3X3 className="h-4 w-4" />
                      <span className="text-sm font-medium">Grade</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Indicador de Filtros Ativos */}
            {(busca || categoriaFiltro) && (
              <div className="mb-6 animate-fade-in">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider mr-1">Filtros ativos:</span>
                  
                  {categoriaFiltro && (
                    <div className="flex items-center gap-1.5 bg-[#fcc3ac]/40 border border-[#fcc3ac]/70 text-[#4a1c0d] px-3 py-1.5 rounded-full text-xs font-bold">
                      <List className="h-3 w-3" />
                      <span>Categoria: {capitalizarPrimeiraLetra(categoriaFiltro)}</span>
                      <button 
                        onClick={() => setCategoriaFiltro('')}
                        className="p-0.5 hover:bg-orange-100 rounded-full transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {busca && (
                    <div className="flex items-center gap-1.5 bg-[#fcc3ac]/40 border border-[#fcc3ac]/70 text-[#4a1c0d] px-3 py-1.5 rounded-full text-xs font-bold">
                      <Search className="h-3 w-3" />
                      <span>Busca: "{busca}"</span>
                      <button 
                        onClick={() => setBusca('')}
                        className="p-0.5 hover:bg-orange-100 rounded-full transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setBusca('');
                      setCategoriaFiltro('');
                      setMostrarTodos(false);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-4 decoration-gray-300 transition-colors px-2 py-1"
                  >
                    Limpar tudo
                  </button>
                </div>
              </div>
            )}

            {/* Produtos por Categoria */}
            {Object.keys(produtosPorCategoria).length > 0 ? (
              <div className="space-y-8 sm:space-y-12 lg:space-y-6">
                {busca.trim() ? (
                  // Modo busca - mostrar todos os produtos encontrados
                  <div className="animate-fade-in">
                    <div className="flex items-center gap-2.5 mb-4 sm:mb-6 lg:mb-3">
                      <div className="h-7 w-1.5 sm:h-8 sm:w-2 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full shadow-md shadow-orange-500/30 flex-shrink-0"></div>
                      <h3 className="text-xl sm:text-2xl lg:text-lg font-black text-white tracking-tight">
                        Resultados para "{busca}"
                      </h3>
                      <span className="text-sm lg:text-xs text-gray-500">
                        ({produtosFiltrados.length} {produtosFiltrados.length === 1 ? 'produto encontrado' : 'produtos encontrados'})
                      </span>
                    </div>
                    <div className={`grid gap-3 sm:gap-6 lg:gap-3 w-full ${visualizacaoMobile === 'lista'
                      ? 'grid-cols-1'
                      : 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                      }`}>
                      {produtosFiltrados.map((produto) => {
                        const quantidadeCard = quantidadesProdutos[produto.id] || 1;
                        const isListaView = visualizacaoMobile === 'lista' && screenWidth < 1024;

                        // Encontrar melhor promoção para este produto
                        const melhorPromocao = promocoes.encontrarMelhorPromocao(
                          produto.nome,
                          produto.categoria_nome,
                          produto.preco
                        );

                        // Calcular preço com desconto
                        const precoOriginal = produto.preco;
                        const precoComDesconto = melhorPromocao
                          ? promocoes.calcularPrecoComDesconto(precoOriginal, melhorPromocao)
                          : precoOriginal;
                        const temDesconto = precoComDesconto < precoOriginal;

                        return (
                          <div
                            key={produto.id}
                            className={`bg-white rounded-2xl border border-gray-200/80 hover:border-[#fcc3ac] shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden min-w-0 ${isListaView
                              ? 'p-2 sm:p-3 flex flex-row gap-2 sm:gap-3'
                              : 'p-2.5 sm:p-3.5 flex flex-col'
                              }`}
                          >
                            {/* Layout horizontal para lista */}
                            {isListaView ? (
                              <>
                                {/* Imagem compacta à esquerda */}
                                <div
                                  className="cursor-pointer flex-shrink-0"
                                  onClick={() => handleProdutoClick(produto)}
                                >
                                  {renderImagemProduto(produto, true)}
                                </div>

                                {/* Conteúdo à direita */}
                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                  {/* Informações do produto */}
                                  <div
                                    className="cursor-pointer overflow-hidden flex-1"
                                    onClick={() => handleProdutoClick(produto)}
                                  >
                                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm lg:text-xs mb-0.5 group-hover:text-[#b85836] transition-colors duration-300 line-clamp-1 break-words overflow-hidden">
                                      {produto.nome}
                                    </h4>
                                    <p className="text-gray-500 text-[9px] sm:text-[10px] lg:text-[9px] mb-1 break-words overflow-hidden">
                                      <span className="text-[#b85836] font-semibold">{produto.categoria_nome}</span>
                                    </p>
                                    {deveExibirDescricao(produto) && (
                                      <p className="text-gray-500 text-[9px] sm:text-[10px] lg:text-[9px] mb-1.5 line-clamp-1 leading-relaxed break-words overflow-hidden">
                                        {produto.descricao}
                                      </p>
                                    )}
                                    {temDesconto ? (
                                      <div className="flex flex-col">
                                        <span className="font-extrabold text-sm sm:text-base lg:text-xs text-gray-900 truncate">
                                          R$ {precoComDesconto.toFixed(2).replace('.', ',')}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-[10px] text-gray-400 line-through">
                                            R$ {precoOriginal.toFixed(2).replace('.', ',')}
                                          </span>
                                          <span className="text-[9px] text-[#4a1c0d] font-bold bg-[#fcc3ac]/50 px-1 py-0.2 rounded border border-[#fcc3ac]">
                                            {melhorPromocao?.tipo === 'percentual'
                                              ? `-${melhorPromocao.valor}%`
                                              : `-R$ ${melhorPromocao?.valor.toFixed(2).replace('.', ',')}`}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="font-extrabold text-sm sm:text-base lg:text-xs text-gray-900 truncate block">
                                        R$ {precoOriginal.toFixed(2).replace('.', ',')}
                                      </span>
                                    )}
                                  </div>

                                  {/* Controles compactos */}
                                  <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-1.5 mt-1.5">
                                    {/* Botões de quantidade */}
                                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 flex-shrink-0 border border-gray-200">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                          }));
                                        }}
                                        className="w-5 h-5 sm:w-6 sm:h-6 lg:w-5 lg:h-5 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Diminuir quantidade"
                                      >
                                        <Minus className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-2.5 lg:w-2.5" />
                                      </button>
                                      <span className="text-[10px] sm:text-xs lg:text-[10px] font-bold text-gray-900 min-w-[16px] sm:min-w-[20px] lg:min-w-[16px] text-center">
                                        {quantidadeCard}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: (prev[produto.id] || 1) + 1
                                          }));
                                        }}
                                        className="w-5 h-5 sm:w-6 sm:h-6 lg:w-5 lg:h-5 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Aumentar quantidade"
                                      >
                                        <Plus className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-2.5 lg:w-2.5" />
                                      </button>
                                    </div>

                                    {/* Botão adicionar ao carrinho */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addToCartFromCard(produto, quantidadeCard);
                                      }}
                                      className="flex-1 bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-1.5 sm:py-2 lg:py-1.5 rounded-lg font-bold text-[9px] sm:text-[10px] lg:text-[9px] hover:brightness-105 transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-1 touch-manipulation min-h-[28px] sm:min-h-[32px] lg:min-h-[28px]"
                                    >
                                      <ShoppingCart className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-2.5 lg:w-2.5 flex-shrink-0" />
                                      <span className="truncate">Adicionar</span>
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Layout vertical para grade */}
                                <div
                                  className="cursor-pointer overflow-hidden mb-1.5 flex flex-col items-center text-center"
                                  onClick={() => handleProdutoClick(produto)}
                                >
                                  {renderImagemProduto(produto, false)}
                                  <span className="text-xs font-bold text-orange-400 bg-orange-500/15 border border-orange-500/30 px-2.5 py-0.5 rounded-full w-fit mb-1.5 inline-block mx-auto text-center tracking-wide">
                                    {produto.categoria_nome}
                                  </span>
                                  <h4 className="font-extrabold text-white text-sm sm:text-base mb-1 group-hover:text-orange-400 transition-colors duration-300 line-clamp-2 min-h-[2.5rem] leading-snug text-center w-full">
                                    {produto.nome}
                                  </h4>
                                  {deveExibirDescricao(produto) && (
                                    <p className="text-slate-400 text-xs mb-1.5 line-clamp-2 leading-relaxed text-center w-full">
                                      {produto.descricao}
                                    </p>
                                  )}
                                </div>

                                {/* Linha de Preço e Controles com Quebra de Linha */}
                                <div className="mt-auto pt-2 border-t border-gray-100 flex flex-col gap-2">
                                  {/* Linha 1: Preço em destaque isolado (sem truncar) */}
                                  <div className="w-full min-w-0 text-center">
                                    {temDesconto ? (
                                      <div className="flex flex-col items-center justify-center min-w-0">
                                        <div className="flex items-baseline justify-center gap-1">
                                          <span className="text-xs font-black text-orange-400">R$</span>
                                          <span className="font-black text-base sm:text-lg text-white leading-tight">
                                            {precoComDesconto.toFixed(2).replace('.', ',')}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-center gap-1.5 mt-0.5">
                                          <span className="text-[11px] text-slate-400 line-through">
                                            R$ {precoOriginal.toFixed(2).replace('.', ',')}
                                          </span>
                                          <span className="text-[10px] text-orange-300 font-bold bg-orange-500/20 px-1.5 py-0.5 rounded border border-orange-500/30">
                                            {melhorPromocao?.tipo === 'percentual'
                                              ? `-${melhorPromocao.valor}%`
                                              : `-R$ ${melhorPromocao?.valor.toFixed(2).replace('.', ',')}`}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-xs font-black text-orange-400">R$</span>
                                        <span className="font-black text-base sm:text-lg text-white leading-tight">
                                          {precoOriginal.toFixed(2).replace('.', ',')}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Linha 2: Seletor de quantidade bem distribuído */}
                                  <div className="flex items-center justify-between bg-gray-100/90 rounded-xl p-1 border border-gray-200/80 w-full">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuantidadesProdutos(prev => ({
                                          ...prev,
                                          [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                        }));
                                      }}
                                      className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                      aria-label="Diminuir quantidade"
                                    >
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="text-xs font-black text-gray-900 px-2 text-center">
                                      {quantidadeCard}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuantidadesProdutos(prev => ({
                                          ...prev,
                                          [produto.id]: (prev[produto.id] || 1) + 1
                                        }));
                                      }}
                                      className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                      aria-label="Aumentar quantidade"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>

                                  {/* Linha 3: Botão Adicionar preenchendo a base */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addToCartFromCard(produto, quantidadeCard);
                                    }}
                                    className="w-full bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-2 px-3 rounded-xl font-bold text-xs hover:brightness-105 active:scale-[0.98] shadow-xs flex items-center justify-center gap-1.5 transition-all touch-manipulation"
                                    title="Adicionar ao carrinho"
                                  >
                                    <ShoppingCart className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>Adicionar</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : mostrarTodos ? (
                  // Modo "Ver Todos" - mostrar todos os produtos em uma única grade
                  <div className="animate-fade-in">
                    <div className="flex items-center gap-2.5 mb-4 sm:mb-6 lg:mb-3">
                      <div className="h-7 w-1.5 sm:h-8 sm:w-2 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full shadow-md shadow-orange-500/30 flex-shrink-0"></div>
                      <h3 className="text-xl sm:text-2xl lg:text-lg font-black text-white tracking-tight">
                        Todos os Produtos
                      </h3>
                    </div>
                    <div className={`grid gap-3 sm:gap-6 lg:gap-3 w-full ${visualizacaoMobile === 'lista'
                      ? 'grid-cols-1'
                      : 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                      }`}>
                      {produtosFiltrados.map((produto) => {
                        const quantidadeCard = quantidadesProdutos[produto.id] || 1;
                        const isListaView = visualizacaoMobile === 'lista' && screenWidth < 1024;
                        return (
                          <div
                            key={produto.id}
                            className={`bg-white rounded-2xl border border-gray-200/80 hover:border-[#fcc3ac] shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden min-w-0 ${isListaView
                              ? 'p-2 sm:p-3 flex flex-row gap-2 sm:gap-3'
                              : 'p-2.5 sm:p-3.5 flex flex-col'
                              }`}
                          >
                            {isListaView ? (
                              <>
                                {/* Layout horizontal para lista */}
                                <div
                                  className="cursor-pointer flex-shrink-0"
                                  onClick={() => handleProdutoClick(produto)}
                                >
                                  {renderImagemProduto(produto, true)}
                                </div>

                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                  <div
                                    className="cursor-pointer overflow-hidden flex-1"
                                    onClick={() => handleProdutoClick(produto)}
                                  >
                                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-0.5 group-hover:text-[#b85836] transition-colors duration-300 line-clamp-1 break-words overflow-hidden">
                                      {produto.nome}
                                    </h4>
                                    <p className="text-gray-500 text-[9px] sm:text-[10px] mb-1 break-words overflow-hidden">
                                      <span className="text-[#b85836] font-semibold">{produto.categoria_nome}</span>
                                    </p>
                                    {deveExibirDescricao(produto) && (
                                      <p className="text-gray-500 text-[9px] sm:text-[10px] mb-1.5 line-clamp-1 leading-relaxed break-words overflow-hidden">
                                        {produto.descricao}
                                      </p>
                                    )}
                                    <span className="font-extrabold text-sm sm:text-base text-gray-900 truncate block">
                                      R$ {produto.preco.toFixed(2).replace('.', ',')}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5">
                                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 flex-shrink-0 border border-gray-200">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                          }));
                                        }}
                                        className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Diminuir quantidade"
                                      >
                                        <Minus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                      </button>
                                      <span className="text-[10px] sm:text-xs font-bold text-gray-900 min-w-[16px] sm:min-w-[20px] text-center">
                                        {quantidadeCard}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: (prev[produto.id] || 1) + 1
                                          }));
                                        }}
                                        className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Aumentar quantidade"
                                      >
                                        <Plus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                      </button>
                                    </div>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addToCartFromCard(produto, quantidadeCard);
                                      }}
                                      className="flex-1 bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-1.5 sm:py-2 rounded-lg font-bold text-[9px] sm:text-[10px] hover:brightness-105 transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-1 touch-manipulation min-h-[28px] sm:min-h-[32px]"
                                    >
                                      <ShoppingCart className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
                                      <span className="truncate">Adicionar</span>
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Layout vertical para grade */}
                                <div
                                  className="cursor-pointer overflow-hidden mb-1.5 flex flex-col items-center text-center"
                                  onClick={() => handleProdutoClick(produto)}
                                >
                                  {renderImagemProduto(produto, false)}
                                  <span className="text-xs font-bold text-orange-400 bg-orange-500/15 border border-orange-500/30 px-2.5 py-0.5 rounded-full w-fit mb-1.5 inline-block mx-auto text-center tracking-wide">
                                    {produto.categoria_nome}
                                  </span>
                                  <h4 className="font-extrabold text-white text-sm sm:text-base mb-1 group-hover:text-orange-400 transition-colors duration-300 line-clamp-2 min-h-[2.5rem] leading-snug text-center w-full">
                                    {produto.nome}
                                  </h4>
                                  {deveExibirDescricao(produto) && (
                                    <p className="text-slate-400 text-xs mb-1.5 line-clamp-2 leading-relaxed text-center w-full">
                                      {produto.descricao}
                                    </p>
                                  )}
                                </div>

                                {/* Linha de Preço e Controles com Quebra de Linha */}
                                <div className="mt-auto pt-2 border-t border-gray-100 flex flex-col gap-2">
                                  {/* Linha 1: Preço em destaque isolado (sem truncar) */}
                                  <div className="w-full min-w-0 text-center">
                                    <div className="flex items-baseline justify-center gap-1">
                                      <span className="text-xs font-black text-orange-400">R$</span>
                                      <span className="font-black text-base sm:text-lg text-white leading-tight">
                                        {produto.preco.toFixed(2).replace('.', ',')}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Linha 2: Seletor de quantidade bem distribuído */}
                                  <div className="flex items-center justify-between bg-gray-100/90 rounded-xl p-1 border border-gray-200/80 w-full">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuantidadesProdutos(prev => ({
                                          ...prev,
                                          [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                        }));
                                      }}
                                      className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                      aria-label="Diminuir quantidade"
                                    >
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="text-xs font-black text-gray-900 px-2 text-center">
                                      {quantidadeCard}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuantidadesProdutos(prev => ({
                                          ...prev,
                                          [produto.id]: (prev[produto.id] || 1) + 1
                                        }));
                                      }}
                                      className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                      aria-label="Aumentar quantidade"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>

                                  {/* Linha 3: Botão Adicionar preenchendo a base */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addToCartFromCard(produto, quantidadeCard);
                                    }}
                                    className="w-full bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-2 px-3 rounded-xl font-bold text-xs hover:brightness-105 active:scale-[0.98] shadow-xs flex items-center justify-center gap-1.5 transition-all touch-manipulation"
                                    title="Adicionar ao carrinho"
                                  >
                                    <ShoppingCart className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>Adicionar</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  // Modo normal - mostrar produtos por categoria
                  Object.entries(produtosPorCategoria).map(([categoria, produtosCategoria]) => (
                    <div key={categoria} className="animate-fade-in">
                      <div className="flex items-center gap-2.5 mb-4 sm:mb-6 lg:mb-3">
                        <div className="h-7 w-1.5 sm:h-8 sm:w-2 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full shadow-md shadow-orange-500/30 flex-shrink-0"></div>
                        <h3 className="text-xl sm:text-2xl lg:text-lg font-black text-white tracking-tight">
                          {capitalizarPrimeiraLetra(categoria)}
                        </h3>
                      </div>
                      <div className={`grid gap-3 sm:gap-6 lg:gap-3 ${visualizacaoMobile === 'lista'
                        ? 'grid-cols-1'
                        : 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        }`}>
                        {(categoriaFiltro
                          ? produtosCategoria
                          : produtosCategoria.slice(0, produtosVisiveisPorCategoria[categoria] || getProdutosIniciais())
                        ).map((produto) => {
                          const quantidadeCard = quantidadesProdutos[produto.id] || 1;
                          const isListaView = visualizacaoMobile === 'lista' && screenWidth < 1024;
                          return (
                            <div
                              key={produto.id}
                              className={`bg-white rounded-2xl border border-gray-200/80 hover:border-[#fcc3ac] shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden min-w-0 ${isListaView
                                ? 'p-2 sm:p-3 flex flex-row gap-2 sm:gap-3'
                                : 'p-2.5 sm:p-3.5 flex flex-col'
                                }`}
                            >
                              {isListaView ? (
                                <>
                                  {/* Layout horizontal para lista */}
                                  <div
                                    className="cursor-pointer flex-shrink-0"
                                    onClick={() => handleProdutoClick(produto)}
                                  >
                                    {renderImagemProduto(produto, true)}
                                  </div>

                                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                                    <div
                                      className="cursor-pointer overflow-hidden flex-1"
                                      onClick={() => handleProdutoClick(produto)}
                                    >
                                      <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-0.5 group-hover:text-[#b85836] transition-colors duration-300 line-clamp-1 break-words overflow-hidden">
                                        {produto.nome}
                                      </h4>
                                      <p className="text-gray-500 text-[9px] sm:text-[10px] mb-1 break-words overflow-hidden">
                                        <span className="text-[#b85836] font-semibold">{produto.categoria_nome}</span>
                                      </p>
                                      {deveExibirDescricao(produto) && (
                                        <p className="text-gray-500 text-[9px] sm:text-[10px] mb-1.5 line-clamp-1 leading-relaxed break-words overflow-hidden">
                                          {produto.descricao}
                                        </p>
                                      )}
                                      <span className="font-extrabold text-sm sm:text-base text-gray-900 truncate block">
                                        R$ {produto.preco.toFixed(2).replace('.', ',')}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5">
                                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 flex-shrink-0 border border-gray-200">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setQuantidadesProdutos(prev => ({
                                              ...prev,
                                              [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                            }));
                                          }}
                                          className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                          aria-label="Diminuir quantidade"
                                        >
                                          <Minus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                        </button>
                                        <span className="text-[10px] sm:text-xs font-bold text-gray-900 min-w-[16px] sm:min-w-[20px] text-center">
                                          {quantidadeCard}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setQuantidadesProdutos(prev => ({
                                              ...prev,
                                              [produto.id]: (prev[produto.id] || 1) + 1
                                            }));
                                          }}
                                          className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded text-gray-800 shadow-xs transition-colors touch-manipulation"
                                          aria-label="Aumentar quantidade"
                                        >
                                          <Plus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                        </button>
                                      </div>

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addToCartFromCard(produto, quantidadeCard);
                                        }}
                                        className="flex-1 bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-1.5 sm:py-2 rounded-lg font-bold text-[9px] sm:text-[10px] hover:brightness-105 transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-1 touch-manipulation min-h-[28px] sm:min-h-[32px]"
                                      >
                                        <ShoppingCart className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
                                        <span className="truncate">Adicionar</span>
                                      </button>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Layout vertical para grade */}
                                  <div
                                    className="cursor-pointer overflow-hidden mb-1.5 flex flex-col items-center text-center"
                                    onClick={() => handleProdutoClick(produto)}
                                  >
                                    {renderImagemProduto(produto, false)}
                                    <span className="text-xs font-bold text-orange-400 bg-orange-500/15 border border-orange-500/30 px-2.5 py-0.5 rounded-full w-fit mb-1.5 inline-block mx-auto text-center tracking-wide">
                                      {produto.categoria_nome}
                                    </span>
                                    <h4 className="font-extrabold text-white text-sm sm:text-base mb-1 group-hover:text-orange-400 transition-colors duration-300 line-clamp-2 min-h-[2.5rem] leading-snug text-center w-full">
                                      {produto.nome}
                                    </h4>
                                    {deveExibirDescricao(produto) && (
                                      <p className="text-slate-400 text-xs mb-1.5 line-clamp-2 leading-relaxed text-center w-full">
                                        {produto.descricao}
                                      </p>
                                    )}
                                  </div>

                                  {/* Linha de Preço e Controles com Quebra de Linha */}
                                  <div className="mt-auto pt-2 border-t border-gray-100 flex flex-col gap-2">
                                    {/* Linha 1: Preço em destaque isolado (sem truncar) */}
                                    <div className="w-full min-w-0 text-center">
                                      <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-xs font-black text-orange-400">R$</span>
                                        <span className="font-black text-base sm:text-lg text-white leading-tight">
                                          {produto.preco.toFixed(2).replace('.', ',')}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Linha 2: Seletor de quantidade bem distribuído */}
                                    <div className="flex items-center justify-between bg-gray-100/90 rounded-xl p-1 border border-gray-200/80 w-full">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: Math.max(1, (prev[produto.id] || 1) - 1)
                                          }));
                                        }}
                                        className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Diminuir quantidade"
                                      >
                                        <Minus className="h-3 w-3" />
                                      </button>
                                      <span className="text-xs font-black text-gray-900 px-2 text-center">
                                        {quantidadeCard}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuantidadesProdutos(prev => ({
                                            ...prev,
                                            [produto.id]: (prev[produto.id] || 1) + 1
                                          }));
                                        }}
                                        className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-white hover:bg-gray-200 active:bg-gray-300 rounded-lg text-gray-800 shadow-xs transition-colors touch-manipulation"
                                        aria-label="Aumentar quantidade"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </button>
                                    </div>

                                    {/* Linha 3: Botão Adicionar preenchendo a base */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addToCartFromCard(produto, quantidadeCard);
                                      }}
                                      className="w-full bg-[#fcc3ac] hover:bg-[#f7b399] text-[#4a1c0d] font-bold border border-[#f5b298]/60 py-2 px-3 rounded-xl font-bold text-xs hover:brightness-105 active:scale-[0.98] shadow-xs flex items-center justify-center gap-1.5 transition-all touch-manipulation"
                                      title="Adicionar ao carrinho"
                                    >
                                      <ShoppingCart className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span>Adicionar</span>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Botão Ver Mais - abaixo dos produtos - só no modo sem filtro de categoria */}
                      {!categoriaFiltro && produtosCategoria.length > getProdutosIniciais() && (
                        <div className="flex justify-center mt-6 sm:mt-8 lg:mt-4">
                          {(() => {
                            const produtosVisiveis = produtosVisiveisPorCategoria[categoria] || getProdutosIniciais();
                            const totalProdutos = produtosCategoria.length;
                            const restantes = totalProdutos - produtosVisiveis;

                            if (produtosVisiveis >= totalProdutos) {
                              return (
                                <button
                                  onClick={() => resetarProdutosVisiveis(categoria)}
                                  className="bg-white hover:bg-gray-100 text-gray-700 px-5 py-2 rounded-full font-bold border border-gray-300 shadow-xs transition-all duration-300 transform active:scale-95 text-xs sm:text-sm flex items-center gap-1.5"
                                >
                                  Ver Menos
                                </button>
                              );
                            } else {
                              return (
                                <button
                                  onClick={() => carregarMaisProdutos(categoria, totalProdutos)}
                                  className="border-2 border-[#fcc3ac] text-[#b85836] bg-white hover:bg-orange-50 px-5 py-2 rounded-full font-bold transition-all duration-300 transform active:scale-95 shadow-xs flex items-center gap-2 text-xs sm:text-sm hover:shadow-md"
                                >
                                  <span>Ver Mais</span>
                                  <span className="bg-[#fcc3ac] text-white text-[10px] px-2 py-0.5 rounded-full font-bold">+{restantes}</span>
                                </button>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-20">
                <div className="bg-white rounded-2xl p-8 sm:p-12 border border-gray-200 shadow-sm">
                  <div className="w-16 h-16 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center mx-auto mb-4 text-[#b85836] shadow-xs">
                    <UtensilsCrossed className="w-8 h-8" />
                  </div>
                  <p className="text-gray-800 text-lg sm:text-xl font-bold">
                    {busca || categoriaFiltro ? 'Nenhum produto encontrado' : 'Nenhum produto disponível'}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {busca || categoriaFiltro ? 'Tente ajustar os filtros de busca.' : 'Aguarde novos produtos serem adicionados.'}
                  </p>
                </div>
              </div>
            )}

            {/* Rodapé Gourmet DK Systems */}
            <div className="mt-14 mb-8 text-center flex flex-col items-center justify-center">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-slate-900/90 border border-white/10 shadow-lg backdrop-blur-md hover:border-amber-500/30 transition-all duration-300 group">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.8)]" />
                <span className="text-xs text-slate-400 font-medium">
                  Desenvolvido por
                </span>
                <span className="text-xs font-black tracking-wider bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent group-hover:brightness-110 transition-all">
                  DK SYSTEMS
                </span>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 font-medium tracking-wide">
                Tecnologia para Delivery &bull; Alta Performance
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Produto - Padrão de Modal Perfeito */}
      {showModal && selectedProduto && (() => {
        // Calcular preço total com personalizações
        const precoPersonalizacoes = Object.entries(selectedOpcoes).reduce((total, [opcaoId, itens]) => {
          return total + itens.reduce((itemTotal, item) => {
            const opcao = opcoes.find(op => op.id === parseInt(opcaoId));
            const opcaoItem = opcao?.itens.find(i => i.id === item.itemId);
            return itemTotal + (opcaoItem?.preco_adicional || 0) * item.quantidade;
          }, 0);
        }, 0);
        const precoTotal = (selectedProduto.preco + precoPersonalizacoes) * quantidade;

        return (
          <div 
            className="fixed inset-0 z-[1100] flex items-center justify-center p-0 sm:p-4 animate-fade-in"
            style={deviceType === 'mobile' ? { backgroundColor: 'rgb(17, 24, 39)' } : { backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
          >
            <div
              className={`bg-gradient-to-br from-gray-800/90 to-gray-900/95 w-full flex flex-col shadow-2xl overflow-hidden ${deviceType !== 'mobile' ? 'rounded-2xl border border-white/10' : ''}`}
              style={{ ...modalMaxHeightStyle, ...modalWidthStyle }}
              data-modal-content
            >
              {/* Header Premium Fixo */}
              <div className="flex justify-between items-center p-4 border-b border-white/5 bg-gray-900/40 backdrop-blur-md sticky top-0 z-20 flex-shrink-0">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <div className="flex flex-col">
                    <h2 className="text-base sm:text-lg font-bold text-white truncate max-w-[200px] sm:max-w-md">
                      {selectedProduto.nome}
                    </h2>
                    <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Detalhes do Item</span>
                  </div>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Conteúdo com scroll robusto */}
              <div className="overflow-y-auto flex-1 p-4 sm:p-6 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }} data-modal-content>
                
                {/* Imagem do produto com overlay gradiente */}
                {selectedProduto.imagem_url && (
                  <div className="relative w-full h-56 sm:h-72 rounded-2xl overflow-hidden mb-6 shadow-2xl border border-white/5 group">
                    <img
                      src={selectedProduto.imagem_url.startsWith('http')
                        ? selectedProduto.imagem_url
                        : `${config.api.baseUrl}${selectedProduto.imagem_url.startsWith('/') ? '' : '/'}${selectedProduto.imagem_url}`}
                      alt={selectedProduto.nome}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                       <span className="px-3 py-1 bg-orange-500/90 text-white text-[10px] font-bold rounded-full uppercase tracking-wider backdrop-blur-sm">
                         {selectedProduto.categoria_nome || 'Destaque'}
                       </span>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Descrição e Preço */}
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                      {selectedProduto.descricao || 'Nenhuma descrição disponível para este produto.'}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Preço Base</span>
                      <span className="text-xl font-bold text-white">
                        R$ {selectedProduto.preco.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Opções de Personalização - Estilo Cards Premium */}
                  {opcoes.length > 0 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-4 w-1 bg-orange-500 rounded-full"></div>
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                          Personalize seu pedido
                        </h3>
                      </div>
                      
                      {opcoes.map((opcao) => (
                        <div 
                          key={opcao.id} 
                          className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden group transition-all duration-300 hover:border-white/10"
                        >
                          {/* Header da Seção */}
                          <div className="p-4 bg-white/5 flex items-center justify-between border-b border-white/5">
                            <div className="flex flex-col">
                              <h4 className="font-bold text-white text-sm">
                                {opcao.nome}
                              </h4>
                              {opcao.obrigatorio && (
                                <span className="text-[10px] font-bold text-orange-400 uppercase tracking-tighter">
                                  Obrigatório
                                </span>
                              )}
                            </div>
                            <div className={`px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-widest ${
                              opcao.tipo === 'adicionar'
                                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}>
                              {opcao.tipo === 'adicionar' ? 'Adicionais' : 'Remover'}
                            </div>
                          </div>

                          {/* Lista de Itens */}
                          <div className="p-2 space-y-1">
                            {opcao.itens.map((item) => {
                              const selectedItem = selectedOpcoes[opcao.id]?.find(s => s.itemId === item.id);
                              const isSelected = !!selectedItem;

                              return (
                                <div
                                  key={item.id}
                                  onClick={() => handleOpcaoChange(opcao.id, item.id, !isSelected)}
                                  className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-300 ${
                                    isSelected
                                      ? 'bg-orange-500/10 border border-orange-500/30'
                                      : 'hover:bg-white/5 border border-transparent'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                      isSelected 
                                        ? 'bg-orange-500 border-orange-500' 
                                        : 'border-white/20'
                                    }`}>
                                      {isSelected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-sm font-medium text-gray-200">{item.nome}</span>
                                      {item.preco_adicional > 0 && (
                                        <span className="text-[10px] font-bold text-green-400">
                                          + R$ {item.preco_adicional.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Qtd para adicionais selecionados */}
                                  {isSelected && opcao.tipo === 'adicionar' && (
                                    <div 
                                      className="flex items-center gap-3 bg-gray-900/60 rounded-lg p-1"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={() => handleQuantidadeChange(opcao.id, item.id, (selectedItem?.quantidade || 1) - 1)}
                                        className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-md text-white transition-all active:scale-90"
                                      >
                                        <Minus className="h-3 w-3" />
                                      </button>
                                      <span className="text-xs font-bold text-white w-4 text-center">
                                        {selectedItem?.quantidade || 1}
                                      </span>
                                      <button
                                        onClick={() => handleQuantidadeChange(opcao.id, item.id, (selectedItem?.quantidade || 1) + 1)}
                                        className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-md text-white transition-all active:scale-90"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Observações */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-1 bg-gray-500 rounded-full"></div>
                      <h3 className="text-sm font-bold text-white uppercase tracking-wider">Alguma observação?</h3>
                    </div>
                    <textarea
                      value={observacoes}
                      onChange={(e) => setObservacoes(e.target.value)}
                      placeholder="Ex: Sem cebola, ponto da carne mal passado, etc..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500/50 outline-none transition-all placeholder:text-gray-600 resize-none h-24"
                    />
                  </div>
                </div>

                {/* Footer Spacer for Mobile */}
                <div className="h-24"></div>
              </div>

              {/* Ações Fixas Rodapé */}
              <div className="p-4 sm:p-6 bg-gray-900/60 backdrop-blur-xl border-t border-white/5 sticky bottom-0 z-20">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* Selector de Qtd Principal */}
                  <div className="flex items-center gap-4 bg-white/5 rounded-2xl p-1.5 border border-white/5 w-full sm:w-auto">
                    <button
                      onClick={() => setQuantidade(Math.max(1, quantidade - 1))}
                      className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl text-white transition-all active:scale-95"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-lg font-bold text-white min-w-[30px] text-center">{quantidade}</span>
                    <button
                      onClick={() => setQuantidade(quantidade + 1)}
                      className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl text-white transition-all active:scale-95"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={addToCart}
                    className="flex-1 w-full bg-gradient-to-r from-orange-500 to-red-600 hover:brightness-110 text-white font-bold py-4 rounded-2xl flex items-center justify-between px-6 shadow-lg shadow-orange-600/20 active:scale-[0.98] transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <ShoppingCart className="h-5 w-5 group-hover:animate-bounce" />
                      <span className="text-sm uppercase tracking-widest">Adicionar</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-white/60 font-medium">Total</span>
                      <span className="text-lg font-black">R$ {precoTotal.toFixed(2)}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal do Carrinho - Padrão de Modal Perfeito */}
      {showCarrinho && (
        <div className="fixed inset-0 z-[1100] flex justify-end pointer-events-none animate-fade-in">
          {/* Overlay Escuro para Tablet/Desktop */}
          {!isMobile && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto" onClick={fecharTodosModais}></div>
          )}
          
          <div className="pointer-events-auto relative z-10 w-full md:w-auto h-full flex flex-col">

          {/* Versão Mobile - Padrão de Modal Perfeito */}
          {isMobile && (
            <div 
              className="fixed inset-0 z-50 flex flex-col bg-gray-950 animate-fade-in" 
              style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
              data-modal-content
            >
              {/* Header Premium Fixo */}
              <div className="flex justify-between items-center p-4 border-b border-white/5 bg-gray-900/40 backdrop-blur-md sticky top-0 z-20 flex-shrink-0">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <div className="flex flex-col">
                    <h3 className="text-base font-bold text-white uppercase tracking-wider">Meu Carrinho</h3>
                    <div className="flex items-center gap-2">
                       <span className="h-1.5 w-1.5 bg-green-500 rounded-full animate-pulse"></span>
                       <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">
                         {cart.itemCount} {cart.itemCount === 1 ? 'item' : 'itens'} selecionados
                       </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Conteúdo com scroll robusto */}
              <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-6 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }} data-modal-content>
                
                {/* Lista de itens - Cards Premium */}
                <div className="space-y-4">
                  {cart.isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-4">
                      <div className="h-10 w-10 border-2 border-t-orange-500 border-white/5 rounded-full animate-spin"></div>
                      <p className="text-xs font-bold uppercase tracking-widest">Atualizando carrinho...</p>
                    </div>
                  ) : !cart.hasItems ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                      <div className="p-6 bg-white/5 rounded-full">
                        <ShoppingCart className="h-12 w-12 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-300 font-bold">Seu carrinho está vazio</p>
                        <p className="text-xs text-gray-500">Escolha produtos deliciosos no menu!</p>
                      </div>
                      <button 
                        onClick={fecharTodosModais}
                        className="px-6 py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-bold text-white uppercase tracking-widest hover:bg-white/10 transition-all"
                      >
                        Voltar ao Menu
                      </button>
                    </div>
                  ) : (
                    cart.cartData.itens.map((item) => (
                      <div key={item.id} className="bg-gray-800/40 backdrop-blur-sm rounded-2xl p-4 border border-white/5 shadow-xl flex flex-col gap-4">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-white text-base leading-tight">
                              {item.produto_nome}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                               <span className="text-xs font-bold text-orange-400">R$ {item.preco_unitario.toFixed(2)}</span>
                               <span className="text-[10px] text-gray-500 uppercase font-bold tracking-tighter">unid.</span>
                            </div>
                            
                            {/* Personalizações com estilo Accordion clean */}
                            {item.personalizacoes && item.personalizacoes.length > 0 && (
                              <div className="mt-3 space-y-1.5 border-l-2 border-white/10 pl-3">
                                {item.personalizacoes.map((personalizacao, idx) => (
                                  <div key={idx} className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-gray-400 flex-1">
                                      {personalizacao.nome} <span className="text-gray-600 font-bold ml-1">x{personalizacao.quantidade}</span>
                                    </span>
                                    <span className="text-[11px] font-bold text-green-500/80">
                                      +R$ {(personalizacao.preco_adicional * personalizacao.quantidade).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Observações */}
                            {item.observacoes && (
                              <div className="mt-3 p-2.5 bg-orange-500/5 rounded-xl border border-orange-500/10 flex items-start gap-2">
                                <div className="mt-0.5">
                                  <span className="flex h-1.5 w-1.5 rounded-full bg-orange-500/50"></span>
                                </div>
                                <p className="text-[11px] text-orange-200/70 italic leading-relaxed">
                                  {item.observacoes}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => {
                                // Fechar modal do carrinho antes de abrir o de customização
                                // para evitar conflito de z-index ou pilha de modais
                                fecharTodosModais();
                                abrirModalCustomizacao(cart.cartData.itens.indexOf(item));
                              }}
                              className="p-2 text-blue-400 hover:text-blue-500 transition-colors bg-white/5 rounded-xl active:scale-90"
                              title="Editar item"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-2 text-gray-500 hover:text-red-500 transition-colors bg-white/5 rounded-xl active:scale-90"
                              title="Remover item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Controles de Quantidade e Subtotal do Item */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/5">
                          <div className="flex items-center gap-3 bg-gray-900/60 rounded-xl p-1 border border-white/5">
                            <button
                               onClick={() => updateQuantity(item.id, item.quantidade - 1)}
                               className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg text-white transition-all active:scale-90"
                            >
                               <Minus className="h-3 w-3" />
                            </button>
                            <span className="text-sm font-bold text-white w-6 text-center">{item.quantidade}</span>
                            <button
                               onClick={() => updateQuantity(item.id, item.quantidade + 1)}
                               className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg text-white transition-all active:scale-90"
                            >
                               <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="text-right">
                             <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Subtotal</p>
                             <p className="text-base font-black text-white">
                               R$ {(item.preco_unitario * item.quantidade + (item.personalizacoes?.reduce((acc, p) => acc + (p.preco_adicional * p.quantidade), 0) || 0) * item.quantidade).toFixed(2)}
                             </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Resumo Financeiro e Endereço */}
                {!cart.isLoading && cart.hasItems && (
                  <div className="space-y-6">
                    {/* Botão de Limpeza com Estilo Sutil */}
                    <button
                      onClick={cart.clearCart}
                      className="w-full py-3 bg-white/5 hover:bg-red-500/10 border border-white/5 hover:border-red-500/20 rounded-2xl text-xs font-bold text-gray-500 hover:text-red-500 transition-all flex items-center justify-center gap-2 uppercase tracking-widest"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Esvaziar Carrinho
                    </button>

                    {/* Componente de Endereço e Frete - Integrado ao design */}
                    <div className="bg-gray-800/40 rounded-3xl border border-white/5 overflow-hidden">
                       <EnderecoEntrega
                        onEnderecoChange={handleEnderecoChange}
                        onFreteChange={handleFreteChange}
                        onAbrirPerfil={() => setShowPerfilModal(true)}
                        entregaGratis={temDireitoEntregaGratis()}
                        valorFaltanteFreteGratis={valorFaltanteParaFreteGratis()}
                        valorTotalItens={cart.cartData.total}
                        tipoEntrega={tipoEntrega}
                        onTipoEntregaChange={(tipo) => setTipoEntrega(tipo)}
                        enderecoAtivo={enderecoEntrega}
                      />
                    </div>
                  </div>
                )}
                
                {/* Footer Spacer for Mobile Navigation */}
                <div className="h-24"></div>
              </div>

              {/* Ações Fixas Rodapé - Checkout */}
              {!cart.isLoading && cart.hasItems && (
                <div className="p-4 bg-gray-900/60 backdrop-blur-xl border-t border-white/5 sticky bottom-0 z-30">
                  <div className="flex flex-col gap-3">
                    {/* Resumo Final Rápido */}
                    <div className="flex items-center justify-between px-2">
                       <div className="flex flex-col">
                          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-none">Total do Pedido</span>
                          <span className="text-2xl font-black text-white">R$ {getTotalCarrinho().toFixed(2)}</span>
                       </div>
                       {enderecoEntrega && (
                         <div className="text-right">
                           <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Entrega</span>
                           <p className={`text-sm font-bold ${valorFrete === 0 ? 'text-green-500' : 'text-white'}`}>
                             {valorFrete === 0 ? 'Grátis' : `R$ ${valorFrete.toFixed(2)}`}
                           </p>
                         </div>
                       )}
                    </div>

                    <button
                      onClick={finalizarPedido}
                      disabled={!cart.hasItems}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 shadow-lg shadow-green-600/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale group"
                    >
                      <Check className="h-5 w-5 group-hover:scale-125 transition-transform" />
                      <span className="text-sm uppercase tracking-widest">
                        Avançar para Checkout
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Versão Tablet - Padrão de Modal Perfeito */}
          {isTablet && !isMobile && (
            <div 
              className="w-[320px] h-full flex flex-col bg-gray-900/95 backdrop-blur-xl border-l border-white/10 shadow-2xl animate-slide-in-right overflow-hidden" 
              data-modal-content
            >
              {/* Header Premium */}
              <div className="flex justify-between items-center p-5 border-b border-white/5 bg-gray-900/40 backdrop-blur-md sticky top-0 z-20">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">Meu Pedido</h3>
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-tighter">
                      {cart.itemCount} {cart.itemCount === 1 ? 'item' : 'itens'} no carrinho
                    </span>
                  </div>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Conteúdo scrollable */}
              <div className="overflow-y-auto flex-1 p-5 space-y-6 custom-scrollbar" data-modal-content>
                {/* Lista de Itens */}
                <div className="space-y-4">
                  {cart.isLoading ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                      <div className="h-8 w-8 border-2 border-t-orange-500 border-white/5 rounded-full animate-spin"></div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Atualizando...</p>
                    </div>
                  ) : !cart.hasItems ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 opacity-50">
                      <ShoppingCart className="h-10 w-10 text-gray-600" />
                      <p className="text-sm font-bold text-gray-400">Carrinho Vazio</p>
                    </div>
                  ) : (
                    cart.cartData.itens.map((item) => (
                      <div key={item.id} className="bg-white/5 rounded-2xl p-4 border border-white/5 hover:border-white/10 transition-all group">
                         <div className="flex justify-between gap-3">
                            <div className="flex-1 min-w-0">
                               <h4 className="font-bold text-white text-sm truncate">{item.produto_nome}</h4>
                               <p className="text-xs text-orange-400 font-medium">R$ {item.preco_unitario.toFixed(2)}</p>

                               {/* Personalizações */}
                               {item.personalizacoes && item.personalizacoes.length > 0 && (
                                <div className="mt-2 space-y-1 border-l border-white/10 pl-2">
                                  {item.personalizacoes.map((personalizacao, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-[10px]">
                                      <span className="text-gray-400">
                                        {personalizacao.nome} <span className="text-gray-600 font-bold">x{personalizacao.quantidade}</span>
                                      </span>
                                      <span className="text-green-500/80">
                                        +R$ {(personalizacao.preco_adicional * personalizacao.quantidade).toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                               )}

                               {/* Observações */}
                               {item.observacoes && (
                                <div className="mt-2 p-2 bg-orange-500/5 rounded-lg border border-orange-500/10 flex items-start gap-1.5">
                                  <span className="mt-1 flex h-1 w-1 rounded-full bg-orange-500/50 flex-shrink-0"></span>
                                  <p className="text-[10px] text-orange-200/70 italic leading-snug break-words">
                                    {item.observacoes}
                                  </p>
                                </div>
                               )}
                            </div>
                            <div className="flex flex-col gap-2">
                               <button
                                 onClick={() => {
                                   fecharTodosModais();
                                   abrirModalCustomizacao(cart.cartData.itens.indexOf(item));
                                 }}
                                 className="text-blue-400 hover:text-blue-300 transition-colors"
                                 title="Editar item"
                               >
                                 <Edit className="h-4 w-4" />
                               </button>
                               <button onClick={() => removeFromCart(item.id)} className="text-gray-500 hover:text-red-400 transition-colors">
                                  <X className="h-4 w-4" />
                               </button>
                            </div>
                         </div>
                         <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-3 bg-gray-950/50 rounded-lg p-1">
                               <button onClick={() => updateQuantity(item.id, item.quantidade - 1)} className="p-1 text-gray-400 hover:text-white"><Minus className="h-3 w-3" /></button>
                               <span className="text-xs font-bold text-white w-4 text-center">{item.quantidade}</span>
                               <button onClick={() => updateQuantity(item.id, item.quantidade + 1)} className="p-1 text-gray-400 hover:text-white"><Plus className="h-3 w-3" /></button>
                            </div>
                            <span className="text-sm font-black text-white">
                              R$ {(item.preco_unitario * item.quantidade + (item.personalizacoes?.reduce((acc, p) => acc + (p.preco_adicional * p.quantidade), 0) || 0) * item.quantidade).toFixed(2)}
                            </span>
                         </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Resumo e Endereço */}
                {!cart.isLoading && cart.hasItems && (
                  <div className="space-y-6 pt-4 border-t border-white/5">
                    <button onClick={cart.clearCart} className="text-[10px] text-gray-500 hover:text-red-400 font-bold uppercase tracking-widest flex items-center gap-1.5 transition-colors">
                      <Trash2 className="h-3 w-3" /> Limpar Carrinho
                    </button>
                    
                    <div className="bg-gray-800/40 rounded-2xl border border-white/5 overflow-hidden">
                       <EnderecoEntrega
                        onEnderecoChange={handleEnderecoChange}
                        onFreteChange={handleFreteChange}
                        onAbrirPerfil={() => setShowPerfilModal(true)}
                        entregaGratis={temDireitoEntregaGratis()}
                        valorFaltanteFreteGratis={valorFaltanteParaFreteGratis()}
                        tipoEntrega={tipoEntrega}
                        onTipoEntregaChange={(tipo) => setTipoEntrega(tipo)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Checkout */}
              {!cart.isLoading && cart.hasItems && (
                <div className="p-5 bg-gray-950 border-t border-white/5">
                   <div className="flex items-center justify-between mb-4 px-1">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Subtotal</span>
                      <span className="text-xl font-black text-white">R$ {getTotalCarrinho().toFixed(2)}</span>
                   </div>
                   <button
                      onClick={finalizarPedido}
                      disabled={!cart.hasItems}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-green-600/20 active:scale-95 transition-all disabled:opacity-50"
                    >
                      <Check className="h-5 w-5" />
                      Avançar para Checkout
                   </button>
                </div>
              )}
            </div>
          )}

          {/* Versão Desktop - Padrão de Modal Perfeito */}
          {!isMobile && !isTablet && (
            <div 
              className="w-[320px] h-full flex flex-col bg-gray-900/90 backdrop-blur-2xl border-l border-white/10 shadow-2xl animate-slide-in-right overflow-hidden" 
              data-modal-content
            >
              <div className="flex justify-between items-center p-4 border-b border-white/5 bg-gray-900/20 backdrop-blur-md">
                <div className="flex flex-col">
                  <h3 className="text-lg font-black text-white tracking-tight">CARRINHO</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="h-0.5 w-6 bg-orange-500 rounded-full"></span>
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{cart.itemCount} itens pronto para você</span>
                  </div>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-500 hover:text-white transition-all p-2 hover:bg-white/5 rounded-xl group"
                >
                  <X className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 p-4 space-y-4 custom-scrollbar" data-modal-content>
                <div className="space-y-4">
                  {cart.isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-30">
                      <div className="h-12 w-12 border-[3px] border-t-orange-500 border-white/5 rounded-full animate-spin"></div>
                    </div>
                  ) : !cart.hasItems ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center space-y-6">
                      <div className="relative">
                        <ShoppingCart className="h-16 w-16 text-gray-800" />
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-orange-500 rounded-full animate-ping"></span>
                      </div>
                      <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Fome detectada: Adicione algo!</p>
                    </div>
                  ) : (
                    cart.cartData.itens.map((item) => (
                      <div key={item.id} className="bg-white/5 rounded-2xl p-4 border border-white/5 hover:border-white/10 transition-all hover:translate-x-1 group relative">
                        <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                          <button
                            onClick={() => {
                              fecharTodosModais();
                              abrirModalCustomizacao(cart.cartData.itens.indexOf(item));
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            title="Editar item"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                          <button onClick={() => removeFromCart(item.id)} className="text-gray-600 hover:text-red-400">
                             <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                        <h4 className="font-bold text-white text-sm mb-0.5 pr-10">{item.produto_nome}</h4>
                        <p className="text-[10px] font-bold text-orange-500 mb-1">R$ {item.preco_unitario.toFixed(2)} cada</p>
                        
                        {/* Personalizações */}
                        {item.personalizacoes && item.personalizacoes.length > 0 && (
                          <div className="mb-3 space-y-1 border-l border-white/10 pl-2">
                            {item.personalizacoes.map((personalizacao, idx) => (
                              <div key={idx} className="flex items-center justify-between text-[9px]">
                                <span className="text-gray-400 truncate max-w-[120px]">
                                  {personalizacao.nome} <span className="text-gray-600 font-bold">x{personalizacao.quantidade}</span>
                                </span>
                                <span className="text-green-500/80">
                                  +R$ {(personalizacao.preco_adicional * personalizacao.quantidade).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Observações */}
                        {item.observacoes && (
                          <div className="mb-3 p-2 bg-orange-500/5 rounded-lg border border-orange-500/10 flex items-start gap-1.5">
                            <span className="mt-1 flex h-1 w-1 rounded-full bg-orange-500/50 flex-shrink-0"></span>
                            <p className="text-[9px] text-orange-200/70 italic leading-snug break-words">
                              {item.observacoes}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                           <div className="flex items-center gap-3 bg-gray-950/60 rounded-xl p-1 border border-white/5">
                              <button onClick={() => updateQuantity(item.id, item.quantidade - 1)} className="w-6 h-6 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg text-white transition-all active:scale-90"><Minus className="h-3 w-3" /></button>
                              <span className="text-xs font-black text-white w-4 text-center">{item.quantidade}</span>
                              <button onClick={() => updateQuantity(item.id, item.quantidade + 1)} className="w-6 h-6 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg text-white transition-all active:scale-90"><Plus className="h-3 w-3" /></button>
                           </div>
                           <div className="text-right">
                              <span className="text-[8px] text-gray-500 font-bold uppercase tracking-widest">Total do Item</span>
                              <p className="text-base font-black text-white">
                                R$ {(item.preco_unitario * item.quantidade + (item.personalizacoes?.reduce((acc, p) => acc + (p.preco_adicional * p.quantidade), 0) || 0) * item.quantidade).toFixed(2)}
                              </p>
                           </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {!cart.isLoading && cart.hasItems && (
                  <div className="space-y-4 pt-4 border-t border-white/5">
                    <div className="bg-gray-800/20 rounded-2xl border border-white/5 p-1 overflow-hidden">
                       <EnderecoEntrega
                        onEnderecoChange={handleEnderecoChange}
                        onFreteChange={handleFreteChange}
                        onAbrirPerfil={() => setShowPerfilModal(true)}
                        entregaGratis={temDireitoEntregaGratis()}
                        valorFaltanteFreteGratis={valorFaltanteParaFreteGratis()}
                        tipoEntrega={tipoEntrega}
                        onTipoEntregaChange={(tipo) => setTipoEntrega(tipo)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {!cart.isLoading && cart.hasItems && (
                <div className="p-6 bg-gray-950 border-t border-white/5">
                   <div className="flex flex-col gap-1 mb-4">
                      <div className="flex justify-between items-center text-gray-500 text-[9px] font-bold uppercase tracking-[0.2em]">
                         <span>Resumo Financeiro</span>
                      </div>
                      <div className="flex justify-between items-end mt-1">
                         <span className="text-gray-400 text-xs">A pagar</span>
                         <span className="text-2xl font-black text-white">R$ {getTotalCarrinho().toFixed(2)}</span>
                      </div>
                   </div>
                   <button
                      onClick={finalizarPedido}
                      disabled={!cart.hasItems}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:brightness-110 text-white font-black py-3.5 rounded-2xl flex items-center justify-center gap-2 shadow-2xl shadow-orange-600/30 active:scale-95 transition-all disabled:opacity-30 disabled:grayscale group"
                    >
                      <ShoppingCart className="h-5 w-5 group-hover:scale-110 transition-transform" />
                      <span className="text-xs uppercase tracking-[0.15em]">
                        AVANÇAR PARA CHECKOUT
                      </span>
                   </button>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Modal Chamar Garçom - Responsivo */}
      {showChamarGarcom && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[1100] animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-sm lg:max-w-md border border-gray-600/50 shadow-2xl overflow-y-auto" style={{ maxHeight: '90vh' }} data-modal-content>
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    Chamar Garçom
                  </h2>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-xs sm:text-sm font-semibold text-gray-300 mb-2">
                  Número da Mesa
                </label>
                <input
                  type="text"
                  value={mesaGarcom}
                  onChange={(e) => setMesaGarcom(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                  placeholder="Digite o número da mesa"
                />
              </div>

              <div className="flex gap-3 sm:gap-4">
                <button
                  onClick={voltarModal}
                  className="flex-1 bg-gray-600 text-white py-2 sm:py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300 text-sm sm:text-base"
                >
                  Cancelar
                </button>
                <button
                  onClick={chamarGarcom}
                  disabled={!mesaGarcom.trim()}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2 sm:py-3 rounded-xl font-bold hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl text-sm sm:text-base"
                >
                  Chamar Garçom
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Perfil - Harmonizado & Responsivo */}
      {showPerfilModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 z-[1100] animate-fade-in">
          <div 
            className="bg-gradient-to-br from-gray-800 to-gray-900 w-full flex flex-col shadow-2xl border-x border-b border-gray-600/50 sm:border sm:rounded-2xl overflow-hidden transition-all duration-300"
            style={{ ...modalMaxHeightStyle, ...modalWidthStyle }}
            data-modal-content
          >
            {/* Header */}
            <div className="p-3 sm:p-5 border-b border-gray-700/50 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-2">
                {(modalStack.length > 1 || editandoPerfil) && (
                  <button
                    onClick={() => {
                      if (editandoPerfil) {
                        setEditandoPerfil(false);
                      } else {
                        voltarModal();
                      }
                    }}
                    className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Voltar"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                )}
                <div>
                  <h2 className="text-base sm:text-xl font-bold text-white leading-tight">Configurações</h2>
                  <p className="text-[10px] sm:text-xs text-gray-400">Gerencie suas informações e conta</p>
                </div>
              </div>
              <button
                onClick={fecharTodosModais}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
              {!editandoPerfil ? (
                // Modo visualização
                <div className="space-y-6">
                  {/* Card Informações Pessoais */}
                  <div className="bg-gray-700/20 backdrop-blur-sm rounded-xl p-4 border border-gray-600/30">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-1.5 bg-orange-500/20 rounded-lg">
                        <User className="h-4 w-4 text-orange-400" />
                      </div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Dados Pessoais</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Nome Completo</p>
                        <p className="text-gray-100 font-medium text-sm">{dadosPerfil.nome} {dadosPerfil.sobrenome}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">E-mail</p>
                        <p className="text-gray-100 font-medium text-sm break-all">{usuario?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Contato e Endereço */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-700/20 backdrop-blur-sm rounded-xl p-4 border border-gray-600/30">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-red-500/20 rounded-lg">
                          <Phone className="h-4 w-4 text-red-400" />
                        </div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Contato</h3>
                      </div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Telefone</p>
                      <p className="text-gray-100 font-medium text-sm">{dadosPerfil.telefone || 'Não informado'}</p>
                    </div>

                    <div className="bg-gray-700/20 backdrop-blur-sm rounded-xl p-4 border border-gray-600/30">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                          <MapPin className="h-4 w-4 text-emerald-400" />
                        </div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Endereço</h3>
                      </div>
                      {dadosPerfil.rua ? (
                        <div className="space-y-0.5">
                          <p className="text-gray-100 font-medium text-sm">{dadosPerfil.rua}, {dadosPerfil.numero_casa}</p>
                          <p className="text-[11px] text-gray-400">{dadosPerfil.bairro}</p>
                          <p className="text-[10px] text-gray-500">{dadosPerfil.cidade} • {dadosPerfil.cep}</p>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic text-xs">Nenhum endereço cadastrado</p>
                      )}
                    </div>
                  </div>

                  {/* Ações Primárias - Estilo Delivery */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <button
                      onClick={() => setEditandoPerfil(true)}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3.5 rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95 group"
                    >
                      <Edit className="h-4 w-4" />
                      Editar Perfil
                    </button>
                    <button
                      onClick={() => {
                        if (!usuario?.id) {
                          toast.error('Você precisa estar logado para ver seus pedidos', { duration: 2000 });
                          setShowPerfilModal(false);
                          return;
                        }
                        setShowHistoricoPedidos(true);
                        resetarPerfil();
                      }}
                      className="flex-1 bg-gray-700 text-white py-3.5 rounded-xl font-bold hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-600 shadow-md active:scale-95 group"
                    >
                      <List className="h-4 w-4" />
                      Ver Pedidos
                    </button>
                  </div>
                </div>
              ) : (
                // Modo edição
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">Nome</label>
                      <input
                        type="text"
                        value={dadosPerfil.nome}
                        onChange={(e) => setDadosPerfil(prev => ({ ...prev, nome: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Nome"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">Sobrenome</label>
                      <input
                        type="text"
                        value={dadosPerfil.sobrenome}
                        onChange={(e) => setDadosPerfil(prev => ({ ...prev, sobrenome: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Sobrenome"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase ml-1">WhatsApp / Telefone</label>
                    <input
                      type="text"
                      value={dadosPerfil.telefone}
                      onChange={(e) => handlePerfilChange('telefone', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">Rua</label>
                      <input
                        type="text"
                        value={dadosPerfil.rua}
                        onChange={(e) => setDadosPerfil(prev => ({ ...prev, rua: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Nome da rua"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">Nº</label>
                      <input
                        type="text"
                        value={dadosPerfil.numero_casa}
                        onChange={(e) => setDadosPerfil(prev => ({ ...prev, numero_casa: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Ex: 123"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">Bairro</label>
                      <input
                        type="text"
                        value={dadosPerfil.bairro}
                        onChange={(e) => setDadosPerfil(prev => ({ ...prev, bairro: e.target.value }))}
                         className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Nome do bairro"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase ml-1">CEP</label>
                      <input
                        type="text"
                        value={dadosPerfil.cep}
                        onChange={(e) => handlePerfilChange('cep', e.target.value)}
                         className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="00000-000"
                      />
                    </div>
                  </div>

                  {/* Ação de Salvar/Cancelar - Coerência */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setEditandoPerfil(false)}
                      className="flex-1 bg-gray-700 text-white py-3 rounded-xl font-bold hover:bg-gray-600 transition-all active:scale-95 border border-gray-600"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={salvarPerfil}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              )}

              {/* Zona de Perigo / Conta */}
              {!editandoPerfil && (
                <div className="mt-8 pt-6 border-t border-gray-700/50 pb-8 sm:pb-0">
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4">Gerenciar Conta</h4>
                  {/* Zona de Perigo / Conta */}
                  <div className="space-y-3">
                    <button
                      onClick={handleLogout}
                      className="w-full p-4 bg-red-500/5 hover:bg-red-500/10 text-red-500 rounded-2xl flex items-center justify-between border border-red-500/10 transition-all group active:scale-[0.98]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-xl">
                          <LogOut className="h-5 w-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-[10px] sm:text-xs uppercase tracking-wider">Sair da Conta</p>
                          <p className="text-[9px] sm:text-[10px] text-red-500/60">Encerrar sessão com segurança</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={handleExcluirConta}
                      className="w-full p-4 bg-gray-700/5 hover:bg-gray-700/10 text-gray-400 rounded-2xl flex items-center justify-between border border-gray-600/10 transition-all group active:scale-[0.98]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-700/10 rounded-xl">
                          <Trash2 className="h-5 w-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-[10px] sm:text-xs uppercase tracking-wider">Excluir Minha Conta</p>
                          <p className="text-[9px] sm:text-[10px] text-gray-500">Ação irreversível de remoção</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Produto (Legado - sobreposto pelo modal Premium acima) */}
      {showModal && selectedProduto && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4 animate-fade-in"
          style={deviceType === 'mobile' ? { backgroundColor: 'rgb(17, 24, 39)' } : { backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className={`bg-gradient-to-br from-gray-800 to-gray-900 w-full flex flex-col shadow-2xl overflow-hidden ${deviceType !== 'mobile' ? 'md:rounded-2xl border border-white/10' : ''}`}
            style={{ ...modalMaxHeightStyle, ...modalWidthStyle }}
            data-modal-content
          >
            {/* Header Mobile Fixo */}
            <div className="flex justify-between items-center p-3 border-b border-gray-600/50 bg-gray-800/80 sticky top-0 z-10 flex-shrink-0">
              <div className="flex items-center gap-2">
                {modalStack.length > 1 && (
                  <button
                    onClick={voltarModal}
                    className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Voltar"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                )}
                <h3 className="text-base font-bold text-white truncate max-w-[200px]">{selectedProduto.nome}</h3>
              </div>
              <button
                onClick={fecharTodosModais}
                className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Sair"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Conteúdo com scroll robusto */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }} data-modal-content>
              {/* Imagem do produto */}
              {selectedProduto.imagem_url && (
                <div className="relative w-full h-48 sm:h-64 lg:h-80 rounded-xl overflow-hidden mb-6 shadow-xl">
                  <img
                    src={selectedProduto.imagem_url.startsWith('http')
                      ? selectedProduto.imagem_url
                      : `${config.api.baseUrl || ''}${selectedProduto.imagem_url.startsWith('/') ? '' : '/'}${selectedProduto.imagem_url}`}
                    alt={selectedProduto.nome}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <p className="absolute bottom-4 left-4 text-white text-2xl sm:text-3xl font-extrabold drop-shadow-lg">
                    {selectedProduto.nome}
                  </p>
                </div>
              )}

              <div className="mb-6">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                  {selectedProduto.descricao}
                </p>
                <p className="text-white text-xl sm:text-2xl font-bold">
                  Preço: R$ {selectedProduto.preco.toFixed(2)}
                </p>
              </div>

              {/* Opções de Personalização */}
              {opcoes.length > 0 && (
                <div className="space-y-6 mb-8">
                  <h3 className="text-lg sm:text-xl font-bold text-white border-b border-gray-700/50 pb-3 mb-4">
                    Personalize seu pedido
                  </h3>
                  {opcoes.map((opcao) => (
                    <div key={opcao.id} className="bg-gradient-to-br from-gray-700/60 to-gray-800/60 rounded-2xl p-4 sm:p-6 border border-gray-600/40 shadow-lg backdrop-blur-sm">
                      {/* Header da seção */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <h4 className="font-bold text-white text-lg">
                            {opcao.nome}
                          </h4>
                          {opcao.obrigatorio && (
                            <span className="bg-[#FFA000]/15 text-[#FFA000] text-xs font-bold px-2.5 py-0.5 rounded-full border border-[#FFA000]/30 shadow-sm">
                              Obrigatório
                            </span>
                          )}
                        </div>

                        <div className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${opcao.tipo === 'adicionar'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                          }`}>
                          {opcao.tipo === 'adicionar' ? (
                            <>
                              <Plus className="w-3.5 h-3.5" />
                              <span>Adicionar</span>
                            </>
                          ) : (
                            <>
                              <Minus className="w-3.5 h-3.5" />
                              <span>Remover</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Lista de itens */}
                      <div className="space-y-3">
                        {opcao.itens.map((item) => {
                          const selectedItem = selectedOpcoes[opcao.id]?.find(selected => selected.itemId === item.id);
                          const isSelected = !!selectedItem;

                          return (
                            <div
                              key={item.id}
                              className={`p-3 rounded-xl border-2 transition-all duration-300 ${isSelected
                                ? opcao.tipo === 'adicionar'
                                  ? 'bg-green-500/10 border-green-500/30 shadow-lg shadow-green-500/10'
                                  : 'bg-orange-500/10 border-orange-500/30 shadow-lg shadow-orange-500/10'
                                : 'bg-gray-600/20 border-gray-600/30 hover:bg-gray-600/30 hover:border-gray-500/50'
                                }`}
                            >
                              {/* Item principal */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <input
                                    type={opcao.max_selecoes > 1 ? 'checkbox' : 'radio'}
                                    name={`personalizacao-${opcao.id}`}
                                    checked={isSelected}
                                    onChange={(e) => handleOpcaoChange(opcao.id, item.id, e.target.checked)}
                                    className={`h-5 w-5 transition-all duration-300 ${opcao.tipo === 'adicionar'
                                      ? 'text-green-500 focus:ring-green-500'
                                      : 'text-orange-500 focus:ring-orange-500'
                                      }`}
                                  />
                                  <span className="font-medium text-white text-base">
                                    {item.nome}
                                  </span>
                                </div>

                                {/* Preço adicional */}
                                {item.preco_adicional > 0 && (
                                  <span className={`font-bold text-base ${opcao.tipo === 'adicionar' ? 'text-green-400' : 'text-orange-400'
                                    }`}>
                                    +R$ {item.preco_adicional.toFixed(2)}
                                  </span>
                                )}
                              </div>

                              {/* Controle de quantidade (apenas para adicionais) */}
                              {isSelected && opcao.tipo === 'adicionar' && (
                                <div className="bg-gray-700/50 rounded-xl p-3 border border-gray-600/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-300 font-medium">
                                      Quantidade
                                    </span>
                                    <span className="text-sm font-bold text-green-400">
                                      Total: R$ {((selectedItem?.quantidade || 1) * item.preco_adicional).toFixed(2)}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-center gap-4">
                                    <button
                                      onClick={() => handleQuantidadeChange(opcao.id, item.id, (selectedItem?.quantidade || 1) - 1)}
                                      className={`p-2 rounded-lg transition-all duration-300 ${(selectedItem?.quantidade || 1) <= 1
                                        ? 'bg-gray-600/50 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-600 hover:bg-gray-500 text-white hover:scale-110'
                                        }`}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>
                                    <span className="text-white text-lg font-bold w-8 text-center">
                                      {selectedItem?.quantidade || 1}
                                    </span>
                                    <button
                                      onClick={() => handleQuantidadeChange(opcao.id, item.id, (selectedItem?.quantidade || 1) + 1)}
                                      className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white transition-all duration-300 hover:scale-110"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Observações */}
              <div className="mb-8">
                <label htmlFor="observacoes" className="block text-lg sm:text-xl font-bold text-white mb-3 border-b border-gray-700/50 pb-3">
                  Observações
                </label>
                <textarea
                  id="observacoes"
                  rows={3}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  className="w-full p-3 bg-gray-700/50 border border-gray-600/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                  placeholder="Ex: Sem cebola, maionese à parte..."
                ></textarea>
              </div>
            </div>

            {/* Footer Fixo com Botão Adicionar */}
            <div className="sticky bottom-0 bg-gradient-to-t from-gray-900 to-gray-800/80 p-4 sm:p-6 border-t border-gray-700/50 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantidade(prev => Math.max(1, prev - 1))}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-all duration-300 hover:scale-110"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span className="text-white text-2xl font-bold w-8 text-center">
                    {quantidade}
                  </span>
                  <button
                    onClick={() => setQuantidade(prev => prev + 1)}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-all duration-300 hover:scale-110"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-white text-2xl font-bold">
                  Total: R$ {precoTotal.toFixed(2)}
                </p>
              </div>
              <button
                onClick={addToCart}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 sm:py-4 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <ShoppingCart className="h-6 w-6" />
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Escolha de Customização */}
      {showModalEscolhaCustomizacao && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-lg lg:max-w-xl max-h-[85vh] overflow-y-auto border border-gray-600/50 shadow-2xl" data-modal-content>
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    Escolha o Lanche para Customizar
                  </h2>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {cart.cartData.itens.map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-700/60 to-gray-800/60 rounded-2xl p-4 sm:p-6 border border-gray-600/40 shadow-lg backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-white text-lg sm:text-xl mb-2">
                          {item.produto_nome}
                        </h3>
                        <p className="text-gray-300 text-sm sm:text-base mb-2">
                          {item.descricao}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>Quantidade: {item.quantidade}</span>
                          <span>Preço: R$ {(item.preco_unitario + item.personalizacoes.reduce((sum: number, p: any) => sum + p.preco_adicional * p.quantidade, 0)).toFixed(2)}</span>
                        </div>
                        {item.personalizacoes.length > 0 && (
                          <div className="mt-3 p-3 bg-gray-600/30 rounded-xl">
                            <p className="text-xs text-gray-400 mb-2 font-medium">Personalizações atuais:</p>
                            <div className="space-y-1">
                              {item.personalizacoes.map((personalizacao: any, idx: number) => (
                                <p key={idx} className="text-xs text-gray-300 flex items-center gap-1">
                                  {personalizacao.tipo === 'adicionar' ? (
                                    <Plus className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                  ) : (
                                    <Minus className="w-3 h-3 text-rose-400 flex-shrink-0" />
                                  )}
                                  <span>{personalizacao.nome} (x{personalizacao.quantidade})</span>
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        {item.imagem_url && (
                          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-600 rounded-xl overflow-hidden shadow-lg">
                            <img
                              src={item.imagem_url.startsWith('http')
                                ? item.imagem_url
                                : `${config.api.baseUrl || ''}${item.imagem_url.startsWith('/') ? '' : '/'}${item.imagem_url}`}
                              alt={item.produto_nome}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => abrirModalCustomizacao(index)}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 sm:py-4 rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    >
                      <Edit className="h-5 w-5" />
                      Customizar Este Lanche
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Customização */}
      {modalCustomizacao.show && modalCustomizacao.item && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-lg lg:max-w-xl max-h-[85vh] overflow-y-auto border border-gray-600/50 shadow-2xl">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                      Customizar: {modalCustomizacao.item.produto_nome || modalCustomizacao.item.produto?.nome}
                    </h2>
                    <p className="text-gray-300 text-sm">
                      Personalize seu lanche adicionando ou removendo ingredientes
                    </p>
                  </div>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Opções de Personalização */}
              {opcoesCustomizacao.length > 0 ? (
                <div className="space-y-6 mb-8">
                  {opcoesCustomizacao.map((opcao) => (
                    <div key={opcao.id} className="bg-gradient-to-br from-gray-700/60 to-gray-800/60 rounded-2xl p-4 sm:p-6 border border-gray-600/40 shadow-lg backdrop-blur-sm">
                      {/* Header da seção */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <h4 className="font-bold text-white text-lg">
                            {opcao.tipo === 'adicionar' ? 'Adicionar Ingredientes' : 'Remover Ingredientes'}
                          </h4>
                        </div>

                        <div className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${opcao.tipo === 'adicionar'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                          }`}>
                          {opcao.tipo === 'adicionar' ? (
                            <>
                              <Plus className="w-3.5 h-3.5" />
                              <span>Adicionar</span>
                            </>
                          ) : (
                            <>
                              <Minus className="w-3.5 h-3.5" />
                              <span>Remover</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Lista de itens */}
                      <div className="space-y-3">
                        {opcao.itens.map((item) => {
                          const selectedItem = personalizacoesCustomizacao[opcao.id]?.find(selected => selected.itemId === item.id);
                          const isSelected = !!selectedItem;

                          return (
                            <div
                              key={item.id}
                              className={`p-3 rounded-xl border-2 transition-all duration-300 ${isSelected
                                ? opcao.tipo === 'adicionar'
                                  ? 'bg-green-500/10 border-green-500/30 shadow-lg shadow-green-500/10'
                                  : 'bg-orange-500/10 border-orange-500/30 shadow-lg shadow-orange-500/10'
                                : 'bg-gray-600/20 border-gray-600/30 hover:bg-gray-600/30 hover:border-gray-500/50'
                                }`}
                            >
                              {/* Item principal */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <input
                                    type={opcao.max_selecoes > 1 ? 'checkbox' : 'radio'}
                                    name={`customizacao-${opcao.id}`}
                                    checked={isSelected}
                                    onChange={(e) => handleOpcaoCustomizacaoChange(opcao.id, item.id, e.target.checked)}
                                    className={`h-5 w-5 transition-all duration-300 ${opcao.tipo === 'adicionar'
                                      ? 'text-green-500 focus:ring-green-500'
                                      : 'text-orange-500 focus:ring-orange-500'
                                      }`}
                                  />
                                  <span className="font-medium text-white text-base">
                                    {item.nome}
                                  </span>
                                </div>

                                {/* Preço adicional */}
                                {item.preco_adicional > 0 && (
                                  <span className={`font-bold text-base ${opcao.tipo === 'adicionar' ? 'text-green-400' : 'text-orange-400'
                                    }`}>
                                    +R$ {item.preco_adicional.toFixed(2)}
                                  </span>
                                )}
                              </div>

                              {/* Controle de quantidade (apenas para adicionais) */}
                              {isSelected && opcao.tipo === 'adicionar' && (
                                <div className="bg-gray-700/50 rounded-xl p-3 border border-gray-600/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-300 font-medium">
                                      Quantidade
                                    </span>
                                    <span className="text-sm font-bold text-green-400">
                                      Total: R$ {((selectedItem?.quantidade || 1) * item.preco_adicional).toFixed(2)}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-center gap-4">
                                    <button
                                      onClick={() => handleQuantidadeCustomizacaoChange(opcao.id, item.id, (selectedItem?.quantidade || 1) - 1)}
                                      className={`p-2 rounded-lg transition-all duration-300 ${(selectedItem?.quantidade || 1) <= 1
                                        ? 'bg-gray-600/50 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-600 hover:bg-gray-500 text-white hover:scale-110'
                                        }`}
                                      disabled={(selectedItem?.quantidade || 1) <= 1}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>

                                    <span className="text-xl font-bold text-white min-w-[30px] text-center">
                                      {selectedItem?.quantidade || 1}
                                    </span>

                                    <button
                                      onClick={() => handleQuantidadeCustomizacaoChange(opcao.id, item.id, (selectedItem?.quantidade || 1) + 1)}
                                      className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-all duration-300 hover:scale-110"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-2xl bg-orange-50/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4 text-[#b85836] shadow-sm">
                    <UtensilsCrossed className="w-7 h-7" />
                  </div>
                  <p className="text-gray-400 text-lg">
                    Este produto não possui opções de personalização disponíveis.
                  </p>
                </div>
              )}

              {/* Observações */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Observações Especiais
                </label>
                <textarea
                  value={observacoesCustomizacao}
                  onChange={(e) => setObservacoesCustomizacao(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="Alguma observação especial para este lanche?"
                />
              </div>

              {/* Botões de ação */}
              <div className="flex gap-4">
                <button
                  onClick={voltarModal}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300"
                >
                  Cancelar
                </button>

                <button
                  onClick={salvarCustomizacao}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Salvar Customizações
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalPagamento
        isOpen={showModalPagamento}
        onClose={fecharTodosModais}
        podeVoltar={modalStack.length > 1}
        onVoltar={voltarModal}
        tipoEntrega={tipoEntrega}
        valorTotal={(() => {
          const temPromocaoFreteGratis = promocoes.temDireitoFreteGratis(cart.cartData.total);
          const temDireitoEntregaGratis = temPromocaoFreteGratis || cart.cartData.entregaGratis;
          const freteFinal = temDireitoEntregaGratis ? 0 : valorFrete;
          const subtotal = cart.cartData.total + freteFinal;
          const descontoCupom = cupomAplicado ? cupomAplicado.desconto : 0;
          return Math.max(0, subtotal - descontoCupom);
        })()}
        onCupomAplicado={(cupom) => setCupomAplicado(cupom)}
        validarCupom={promocoes.validarCupom}
        calcularDescontoCupom={promocoes.calcularDescontoCupom}
        onPagamentoConfirmado={handlePagamentoConfirmado}
        usuario={usuario}
        dadosCarrinho={{
          itens: cart.cartData.itens.map(item => ({
            produto_id: item.produto_id,
            quantidade: item.quantidade,
            preco_unitario: item.preco_unitario,
            observacoes: item.observacoes,
            personalizacoes: item.personalizacoes
              .filter(p => p.opcao_id && p.item_id && p.opcao_id > 0 && p.item_id > 0)
              .map(p => ({
                opcao_id: p.opcao_id,
                item_id: p.item_id,
                nome: p.nome || '',
                tipo: 'adicionar',
                quantidade: p.quantidade || 1,
                preco_adicional: p.preco_adicional || 0
              }))
          })),
          observacoes: cart.cartData.carrinho?.observacoes || '',
          valorFrete: valorFrete,
          enderecoEntrega: enderecoEntrega,
          usuario: usuario
        }}
      />

      {/* Modal de Histórico de Pedidos */}
      {usuario && (
        <HistoricoPedidos
          isOpen={showHistoricoPedidos}
          onClose={fecharTodosModais}
          podeVoltar={modalStack.length > 1}
          onVoltar={voltarModal}
          deviceType={deviceType}
          modalMaxHeightStyle={modalMaxHeightStyle}
          modalWidthStyle={modalWidthStyle}
        />
      )}

      {/* Modal de Confirmação de Exclusão de Conta */}
      {showExcluirContaModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-sm lg:max-w-md border border-gray-600/50 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <h3 className="text-lg font-bold text-white">
                    Excluir Conta
                  </h3>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <X className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h4 className="text-md font-bold text-white">
                    Ação irreversível
                  </h4>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="mb-6">
                <p className="text-white mb-4">
                  Tem certeza que deseja <span className="font-bold text-red-400">excluir sua conta</span> permanentemente?
                </p>

                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-red-300 text-sm">
                        Todos os seus dados pessoais serão removidos
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-red-300 text-sm">
                        Seu histórico de pedidos será mantido para fins de auditoria
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-red-300 text-sm">
                        Esta ação não pode ser desfeita
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-sm text-yellow-300">
                    <span className="font-semibold">Atenção:</span> Se você tiver pedidos pendentes, será necessário cancelá-los primeiro.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={cancelarExclusaoConta}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarExclusaoConta}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-xl font-bold hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Excluir Conta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Loja Fechada */}
      {showModalLojaFechada && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-sm lg:max-w-md border border-gray-600/50 shadow-2xl max-h-[85vh] overflow-y-auto" data-modal-content>
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <h3 className="text-lg font-bold text-white">
                    Loja Fechada
                  </h3>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">
                    Fora do horário de funcionamento
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="mb-6">
                <p className="text-white mb-4">
                  Desculpe, nossa loja está <span className="font-bold text-red-400">fechada no momento</span>.
                </p>

                {statusLoja?.horarioHoje && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-red-300 text-sm">
                          <span className="font-semibold">Hoje:</span> {statusLoja.horarioHoje.fechado ? 'Loja fechada' : (() => {
                            // Verificar se o horário cruza a meia-noite
                            const converterParaMinutos = (horario: string) => {
                              const [hora, minuto] = horario.split(':').map(Number);
                              return hora * 60 + minuto;
                            };

                            const aberturaMinutos = converterParaMinutos(statusLoja.horarioHoje.abertura);
                            const fechamentoMinutos = converterParaMinutos(statusLoja.horarioHoje.fechamento);

                            if (fechamentoMinutos < aberturaMinutos) {
                              return `${statusLoja.horarioHoje.abertura} às ${statusLoja.horarioHoje.fechamento} (dia seguinte)`;
                            } else {
                              return `${statusLoja.horarioHoje.abertura} às ${statusLoja.horarioHoje.fechamento}`;
                            }
                          })()}
                        </span>
                      </div>
                      {statusLoja?.proximaAbertura && (
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span className="text-orange-300 text-sm">
                            <span className="font-semibold">Próxima abertura:</span> {statusLoja.proximaAbertura.dia} às {statusLoja.proximaAbertura.horario}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-[#fcc3ac]/10 border border-[#fcc3ac]/25 rounded-xl p-3">
                  <p className="text-sm text-[#FF7A40]">
                    <span className="font-bold text-[#FFA000]">Dica:</span> Você pode continuar navegando pelo menu, mas só poderá fazer pedidos quando a loja estiver aberta.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={fecharTodosModais}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300"
                >
                  Continuar Navegando
                </button>
                <button
                  onClick={() => {
                    setShowModalLojaFechada(false);
                    abrirWhatsApp();
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                  </svg>
                  Falar no WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Modal de Login Necessário */}
      {showLoginCard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-sm lg:max-w-md border border-gray-600/50 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {modalStack.length > 1 && (
                    <button
                      onClick={voltarModal}
                      className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                      aria-label="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <h3 className="text-lg font-bold text-white">
                    Login Necessário
                  </h3>
                </div>
                <button
                  onClick={fecharTodosModais}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  aria-label="Sair"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <h4 className="text-md font-bold text-white">
                    Autenticação requerida
                  </h4>
                  <p className="text-sm text-gray-400">
                    Para adicionar ao carrinho
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="mb-6">
                <p className="text-white mb-4">
                  Para adicionar produtos ao carrinho e finalizar seu pedido, você precisa estar logado.
                </p>

                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-orange-300 text-sm">
                        Acesse seu histórico de pedidos
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-orange-300 text-sm">
                        Configure seu endereço de entrega
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-orange-300 text-sm">
                        Finalize pedidos com segurança
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#fcc3ac]/10 border border-[#fcc3ac]/25 rounded-xl p-3">
                  <p className="text-sm text-[#FF7A40]">
                    <span className="font-bold text-[#FFA000]">Dica:</span> Você pode continuar navegando sem fazer login, mas precisará se autenticar para finalizar o pedido.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={fecharTodosModais}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300"
                >
                  Continuar Navegando
                </button>
                <button
                  onClick={() => {
                    setShowLoginCard(false);
                    navigate('/login', { state: { from: { pathname: `/menu/${empresaId}` } } });
                  }}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <User className="h-4 w-4" />
                  Fazer Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botão Flutuante do WhatsApp */}
      <div className="whatsapp-button">
        <button
          onClick={abrirWhatsApp}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-3 rounded-full shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-110 animate-pulse hover:animate-none group relative"
          title="Falar no WhatsApp"
          style={{ position: 'relative' }}
        >
          {/* Ícone do WhatsApp */}
          <svg className="h-6 w-6 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
          </svg>

          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
            Falar no WhatsApp
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(100px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }

        /* Estilos para elementos details/summary no carrinho */
        details[class*="group"] summary {
          cursor: pointer;
          user-select: none;
          transition: all 0.2s ease;
        }

        details[class*="group"] summary:hover {
          opacity: 0.8;
        }

        details[class*="group"] summary::-webkit-details-marker {
          display: none;
        }

        details[class*="group"][open] summary {
          margin-bottom: 0.5rem;
        }

        /* Estilos específicos para o scroll do carrinho */
        .carrinho-scroll::-webkit-scrollbar {
          width: 6px;
        }
        
        .carrinho-scroll::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 3px;
        }
        
        .carrinho-scroll::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.6);
          border-radius: 3px;
          transition: background 0.3s ease;
        }
        
        .carrinho-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.8);
        }

        /* Firefox scrollbar para o carrinho */
        .carrinho-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.6) rgba(55, 65, 81, 0.3);
        }

        /* Melhorias de scroll para mobile */
        @media (max-width: 1023px) {
          /* Prevenir scroll duplo no mobile */
          html, body {
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y;
          }
          
          /* Garantir scroll natural no container principal */
          body {
            overflow-x: hidden;
            position: relative;
          }
          
          /* Melhorar performance de scroll */
          * {
            -webkit-tap-highlight-color: transparent;
          }
          
          /* Prevenir bounce no iOS */
          .overflow-x-hidden {
            overscroll-behavior-x: none;
          }
        }

        /* Responsividade para diferentes tamanhos de tela */
        @media (max-width: 640px) {
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        }

        /* Estilos para scroll independente */
        @media (min-width: 1024px) {
          /* Customizar scrollbar da sidebar */
          .lg\\:overflow-y-auto::-webkit-scrollbar {
            width: 6px;
          }
          
          .lg\\:overflow-y-auto::-webkit-scrollbar-track {
            background: rgba(55, 65, 81, 0.2);
            border-radius: 3px;
          }
          
          .lg\\:overflow-y-auto::-webkit-scrollbar-thumb {
            background: rgba(156, 163, 175, 0.4);
            border-radius: 3px;
            transition: background 0.3s ease;
          }
          
          .lg\\:overflow-y-auto::-webkit-scrollbar-thumb:hover {
            background: rgba(156, 163, 175, 0.6);
          }

          /* Customizar scrollbar do conteúdo principal */
          .overflow-y-auto::-webkit-scrollbar {
            width: 12px;
          }
          
          .overflow-y-auto::-webkit-scrollbar-track {
            background: rgba(31, 41, 55, 0.3);
            border-radius: 6px;
          }
          
          .overflow-y-auto::-webkit-scrollbar-thumb {
            background: rgba(156, 163, 175, 0.6);
            border-radius: 6px;
            border: 2px solid transparent;
            background-clip: content-box;
            transition: background 0.3s ease;
          }
          
          .overflow-y-auto::-webkit-scrollbar-thumb:hover {
            background: rgba(156, 163, 175, 0.8);
            background-clip: content-box;
          }

          /* Firefox scrollbar */
          .lg\\:overflow-y-auto,
          .overflow-y-auto {
            scrollbar-width: thin;
            scrollbar-color: rgba(156, 163, 175, 0.4) rgba(55, 65, 81, 0.2);
          }

          /* Garantir que o sidebar tenha scroll quando necessário */
          .lg\\:h-screen.lg\\:overflow-y-auto {
            overflow-y: auto !important;
            scrollbar-gutter: stable;
          }

          /* Forçar scroll no container das categorias */
          .lg\\:flex.lg\\:flex-col .lg\\:overflow-y-auto {
            overflow-y: auto !important;
            max-height: calc(100vh - 200px);
            scrollbar-gutter: stable;
          }

          /* Espaçamento entre categorias */
          .space-y-4 > * + * {
            margin-top: 1rem;
          }

          /* Margem inferior na última categoria */
          .pb-10 {
            padding-bottom: 2.5rem;
          }
        }

        /* Garantir que o header fique sempre visível - Scoped to avoid affecting sidebar sticky top */
        [data-header-container].fixed {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }

        /* Otimizar performance de scroll */
        .overflow-y-auto,
        .lg\\:overflow-y-auto {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
        }

        /* Layout fixo para desktop */
        @media (min-width: 1024px) {
          .lg\\:sticky {
            position: sticky !important;
          }
          
          .lg\\:h-full {
            height: 100%;
          }
        }

        /* Mobile layout */
        @media (max-width: 1023px) {
          .fixed {
            position: fixed;
          }
        }

        /* Estilos específicos para o botão do WhatsApp */
        .whatsapp-button {
          position: fixed !important;
          bottom: 24px !important;
          right: 24px !important;
          z-index: 1000 !important;
        }

        .whatsapp-button button {
          position: relative !important;
          width: 56px !important;
          height: 56px !important;
          padding: 12px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .whatsapp-button button svg {
          width: 24px !important;
          height: 24px !important;
        }

        /* Ajustes para iPad e desktop - manter tamanho consistente */
        @media (min-width: 768px) {
          .whatsapp-button {
            bottom: 32px !important;
            right: 32px !important;
          }
        }

        @media (min-width: 1024px) {
          .whatsapp-button {
            bottom: 40px !important;
            right: 40px !important;
          }
        }

        /* Otimizações específicas para iPhone 14 */
        @media (max-width: 430px) and (max-height: 932px) {
          /* iPhone 14 Pro Max */
          .carrinho-scroll {
            padding-bottom: 20px !important;
          }
          
          .carrinho-scroll::-webkit-scrollbar {
            width: 4px;
          }
        }

        @media (max-width: 390px) and (max-height: 844px) {
          /* iPhone 14 */
          .carrinho-scroll {
            padding-bottom: 18px !important;
          }
          
          /* Ajustar espaçamento do botão finalizar */
          .carrinho-scroll .h-24 {
            height: 22px !important;
          }
        }

        /* Suporte para safe-area-inset em dispositivos iOS */
        @supports (padding: max(0px)) {
          .carrinho-scroll {
            padding-bottom: max(16px, env(safe-area-inset-bottom)) !important;
          }
        }


        /* Melhorar scroll no iOS */
        .carrinho-scroll {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        /* Garantir que o botão finalizar seja sempre acessível no iPhone */
        @media (max-width: 430px) {
          .carrinho-scroll .h-24 {
            height: 28px !important;
          }
        }

        /* Correções específicas de scroll para mobile */
        @media (max-width: 1023px) {
          /* Garantir scroll natural no body */
          html {
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
          }
          
          body {
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
            position: relative;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Container principal deve permitir scroll */
          .block.min-h-\\[calc\\(100vh-var\\(--header-height\\)\\)\\] {
            min-height: calc(100vh - var(--header-height, 110px));
            height: auto;
            display: block;
          }
          
          /* Conteúdo principal deve permitir scroll natural */
          .w-full.overflow-x-hidden {
            width: 100%;
            min-height: calc(100vh - var(--header-height, 110px));
            overflow-y: visible;
            overflow-x: hidden;
            display: block;
          }
          
          /* Prevenir problemas de scroll em elementos fixos */
          .fixed {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            will-change: transform;
            backface-visibility: hidden;
          }
          
          /* Melhorar performance de scroll */
          * {
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
        }
      `}</style>
    </div>
  );
};

export default MenuPublico;
